// =============================================================================
// Mobile touch-target tokens.
// WCAG 2.1 AA requires ≥44×44px touch targets. Use these on ANY clickable
// element that would otherwise be under 44px on mobile. Pattern is always:
// "bigger on mobile, current size on desktop (≥768px)".
// =============================================================================

/** 44px mobile / 40px desktop — default icon button (replaces loose `size-10`). */
export const MOBILE_ICON_BUTTON = 'h-11 w-11 md:h-10 md:w-10';

/** 40px mobile / 32px desktop — compact icon button for toolbars (replaces `size-8`). */
export const MOBILE_ICON_BUTTON_SM = 'h-10 w-10 md:h-8 md:w-8';

/** 40px mobile / 28px desktop — the smallest we allow (for dense card actions). */
export const MOBILE_ICON_BUTTON_XS = 'h-9 w-9 md:h-7 md:w-7';

/** 36px mobile / 32px desktop — pill/chip height. Was h-11 (44px), which read
 *  as a tall, heavy pill on mobile vs the compact iOS-app pills (live review). */
export const MOBILE_PILL_HEIGHT = 'h-9 md:h-8';

/** 32px mobile / 28px desktop — filter-chip height. Was h-10 (40px). */
export const MOBILE_CHIP_HEIGHT = 'h-8 md:h-7';

/** Invisible 44px hit-area via ::before — use when visual size must stay small. */
export const MOBILE_HIT_AREA =
  "relative before:absolute before:inset-0 before:content-[''] before:md:hidden before:-m-[max(0px,calc((44px-100%)/2))]";

/**
 * 44px touch target for a one-line inline text button ("Show last 90 days",
 * "Connect", a summary line's trigger), without changing its visual line.
 * `MOBILE_HIT_AREA`'s negative margin is a percentage, which resolves against
 * the WIDTH of the containing block, so it never grows a wide, short control; this
 * one grows the invisible target vertically by a fixed 14px each side
 * (16 + 28 = 44) and hides itself from `md`, where a pointer is precise.
 */
export const INLINE_TEXT_HIT_AREA =
  "relative before:absolute before:inset-x-0 before:-inset-y-3.5 before:content-[''] before:md:hidden";

export const INLINE_TEXT_HIT_AREA_BELOW =
  "relative before:absolute before:inset-x-0 before:-bottom-3.5 before:-top-1 before:content-[''] before:md:hidden";

/**
 * THE toolbar facet trigger — a Select/filter control above a data table.
 *
 * 🔴 `rounded-md` (10px), NOT `rounded-full`. Radius encodes object class in
 * this app: `rounded-full` means ACTION (the `+ Post` / `New campaign` pill
 * shape), `rounded-md` means DATA CONTROL — the search field, the table frame,
 * and this. A facet is a FIELD that happens to open a menu, so dressing it as a
 * pill puts it in the primary CTA's costume; that is precisely why a rounded
 * facet carrying an icon "looks like a primary button" (founder, 2026-08-23).
 *
 * shadcn agrees by construction: `DataTableFacetedFilter` is
 * `Button variant="outline" size="sm" className="h-8 border-dashed"` — default
 * `rounded-md`, never a pill.
 *
 * Full reasoning: `docs/design-system/TOOLBAR-DOCTRINE.md` §12.
 */
export const FACET_TRIGGER =
  'h-8 gap-1.5 rounded-md bg-transparent px-2.5 text-detail font-normal border-border/60 ' +
  MOBILE_HIT_AREA;

/** One panel and one row grammar for the three Insights toolbar menus. */
export const ANALYTICS_MENU_PANEL = 'max-h-96 w-80 overflow-y-auto p-1.5';
export const ANALYTICS_MENU_ROW =
  'flex min-h-11 w-full min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-9 sm:py-1.5';
export const ANALYTICS_MENU_LABEL = 'text-caption font-normal text-foreground';
export const ANALYTICS_MENU_META = 'text-detail text-muted-foreground';

/**
 * Action button (outline/flat `<Button>`) that sits directly next to/under a
 * SELECTABLE_PILL row (a settings SectionHeader action, a format-picker CTA,
 * etc). Steps the Button down to the pill's visual scale — text-detail,
 * font-normal, translucent border — so it never dwarfs the pills.
 * MOBILE_HIT_AREA restores the 44px mobile tap target the shrunk box would
 * otherwise lose (SELECTABLE_PILL is scoped to toggles only; this is its
 * action-button sibling). Pair with `size="compact"`.
 *
 * NOT the same object as {@link FACET_TRIGGER}: this is an ACTION and keeps
 * `rounded-full`; a facet is a data control and is `rounded-md`. See
 * TOOLBAR-DOCTRINE §12.1.
 */
export const PILL_ROW_ACTION =
  // `bg-transparent` overrides Button `variant="outline"`'s `bg-background`,
  // which is a real fill and made this pill read heavier than the capsule and
  // the search field it sits beside. See TABLE_DESKTOP_ACTION_GROUP for the
  // one-toolbar-one-family rule this is half of.
  'h-8 rounded-full bg-transparent px-3 text-detail font-normal border-border/60 ' +
  MOBILE_HIT_AREA;

/**
 * Generic dropdown-menu row bumped toward a 40px mobile tap target via
 * PADDING, NOT glyph inflation — the default `DropdownMenuItem` row sits at
 * ~29px on mobile with no responsive split. Icons need no size override:
 * DropdownMenuItem's base already forces `[&_svg]:size-4` (16px) on every
 * descendant icon via CSS specificity, regardless of any explicit size-3.5
 * on the icon itself. DISTINCT from SIDEBAR_POPOVER_* below, which
 * deliberately jumps icon/text further to match one specific oversized
 * sidebar row — this is the generic case for any other app dropdown.
 */
export const MENU_ITEM_MOBILE = 'gap-2 md:gap-1.5 px-2 py-2.5 md:py-1.5 text-sm md:text-caption';

/**
 * Composer chrome pill/chip height (ModePill, ComposerModeModelMenu trigger,
 * BrandKitChip). Mobile bumped to match the `composer-icon` Button size
 * (Send/Add/Skills/Voice) sitting in the same toolbar row — that size moved
 * to the nav-icon-mobile token (40px, was 36px) in the same round as this
 * fix, so this must track it or the two bumps un-converge again; desktop
 * stays 28px, where everything already converges.
 */
export const COMPOSER_CHIP_HEIGHT = 'h-10 md:h-7';

/**
 * Sidebar-adjacent popover row (the "More" flyout, user-account menu, and
 * PlatformPlugMenu) — bumps text/icon to the sidebar ROW's mobile scale so a
 * menu opened FROM the sidebar reads at the size of the row that triggered
 * it. Desktop is untouched by these three fragments (append the menu's own
 * md: text size, e.g. `md:text-caption`, at the call site). SCOPED to these
 * named sidebar popovers — never apply to a generic app dropdown, which
 * would mean editing the shared DropdownMenuItem base. Real row height (not
 * MOBILE_HIT_AREA) supplies the 44px target: MOBILE_HIT_AREA's invisible
 * ::before would overlap neighbors in a stacked list.
 */
export const SIDEBAR_POPOVER_ITEM = 'gap-3 md:gap-2 px-2 py-2.5 md:py-1.5';
/** Icon inside a SIDEBAR_POPOVER_ITEM. */
export const SIDEBAR_POPOVER_ICON = 'size-6 md:size-3.5';
/** Label inside a SIDEBAR_POPOVER_ITEM — append the call site's own md: size. */
export const SIDEBAR_POPOVER_LABEL = 'text-sidebar-nav-mobile font-normal text-foreground/85';

/**
 * The chrome shell every right-side panel header shares: 48px tall, one hairline
 * rule, a hair of card tint.
 *
 * It was hand-typed in three places and had already drifted — WorkflowTrackerBody
 * dropped `bg-card/40` and used `gap-2`, despite rendering INSIDE a FloatingPanel,
 * so the workflow tracker's header was visibly untinted next to every sibling
 * panel. One export, so the next drift is impossible rather than merely discouraged.
 *
 * Deliberately NOT used by InspectorHeader: it is a different layout (back-arrow
 * left, centred title, no rule), not a drifted copy of this one. Forcing it in
 * would be false consistency.
 */
export const PANEL_HEADER_SHELL =
  'flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border/40 bg-card/40 px-4';

/** Title inside a PANEL_HEADER_SHELL. `truncate-fade` (not `truncate`) is the
 *  Header Law treatment — every other truncating title in the app fades. */
export const PANEL_HEADER_TITLE = 'truncate-fade min-w-0 text-sm font-medium text-foreground/85';

/**
 * Sectioned panel container with FULL-BLEED dividers between sections.
 * Use when the sections are visually similar and the rhythm reads as a list
 * (e.g. Discover sidebar widget stack).
 */
export const PANEL_CONTAINER =
  'rounded-xl border border-border/60 bg-card/40 divide-y divide-border/40 overflow-hidden';

/**
 * Sectioned panel container expecting INSET dividers (rendered explicitly
 * via `<Separator className={PANEL_DIVIDER_INSET} />`). Use when sections
 * have varied content density and a full-bleed line would read as a heavy
 * structural seam (e.g. Plan Detail right column: Audience / Instructions
 * / Files have very different heights). The inset divider behaves like a
 * typographic ruling instead of a chrome edge.
 */
export const PANEL_CONTAINER_INSET =
  'rounded-xl border border-border/60 bg-card/40 overflow-hidden';

/**
 * Inset divider between sections inside a `PANEL_CONTAINER_INSET`. Mirrors
 * the section's `p-5` horizontal padding so the divider sits flush with
 * the inner content column. Children that conditionally render (return
 * `null` when empty) MUST own their own LEADING `<Separator>` so an
 * empty child does not leave an orphan divider in the parent's stack.
 */
export const PANEL_DIVIDER_INSET = 'mx-5 w-auto bg-border/40';

/**
 * The PRIMARY sentence-case heading over a group of content — the top level
 * inside a page's content column. One full size step above the field labels
 * beneath it, because hierarchy here has to survive being read, not measured.
 *
 * 14px/500 over a 12px/500 {@link FIELD_LABEL}. This token was 16px/500 for
 * one day (2026-08-26) and that was COMPENSATION, not hierarchy: FIELD_LABEL
 * was 14px/500 at the time, so a 14px heading was the same size AND the same
 * weight as its own labels, and the whole hierarchy was a 30% opacity step —
 * four heading/label pairs on one Concept screen read as one wall. Shrinking
 * the label to 12px removes the collision at its source and takes a whole size
 * step out of every page, which is the point.
 *
 * Two forms were rendered on that route and rejected on the evidence, and both
 * are still wrong at this size:
 * - `text-sm font-normal text-foreground/85` (the value DESIGN-TOKENS.md §2.5
 *   locks) INVERTS the hierarchy — at 400 the heading reads LIGHTER than the
 *   500-weight field labels it introduces, so the section head recedes below
 *   its own fields. Weight must not step DOWN into the labels; only size does.
 * - `text-sm font-semibold` is a barely-perceptible 100-weight step at 14px,
 *   did not separate the levels on screen, and is banned for section titles by
 *   DESIGN-PATTERNS.md §4 anyway.
 *
 * Size is the correct axis per DESIGN-PATTERNS.md §3.2 ("hierarchy comes from
 * size and weight, never from color").
 *
 * NOT the page title: `PageHeader` deliberately renders its `h1` at 14px on
 * desktop because it is chrome in the top bar, not a heading in the content
 * column. These two never sit in the same plane.
 *
 * ⚠️ One level only. A heading NESTED inside a section that already has a
 * SECTION_TITLE is the level below and belongs to
 * {@link SETTINGS_SECTION_HEADING}. Reaching for this token at every level is
 * how the wall got built the first time.
 */
export const SECTION_TITLE = 'text-sm font-medium tracking-tight text-foreground';

/**
 * Canonical group label — the small quiet title above a group of rows in menus,
 * popovers, rails, and card sections (e.g. "Mode", "Model", "Output", "Skills",
 * "Recommended"). Sentence case since 2026-11-03: caps and tracking read as a loud
 * object at this size and do nothing a quiet colour does not. This is the single source
 * of truth that replaces ~9 duplicated per-file `SECTION_LABEL`/`EYEBROW_CLS`
 * constants. (Distinct from {@link SECTION_TITLE}, which is the sentence-case
 * panel heading.) TEXT classes only — supply padding at the call site
 * (`<DropdownMenuLabel>` already adds `px-2 py-1.5`; plain divs wrap with
 * `cn('px-2 py-1.5', SECTION_LABEL)`).
 */
export const SECTION_LABEL = 'text-caption font-medium text-muted-foreground';

/**
 * The 10px sibling of {@link SECTION_LABEL} — same tracked-out uppercase
 * treatment one size down, for tighter contexts (wizard strips, card metadata
 * rows, tool-view sub-headers). Full-opacity muted, the same colour as
 * {@link SECTION_LABEL}; no step-down opacity, which dropped below 3:1. Replaces ~27 duplicated inline
 * copies. Only for the NEUTRAL `text-muted-foreground` form — labels on colored
 * or inverted surfaces keep their context-specific color inline.
 */
export const MICRO_LABEL = 'text-micro font-medium uppercase tracking-wider text-muted-foreground';

/**
 * Form-field label — sentence case, no tracking. The label directly above an
 * input, textarea, select, or segmented control.
 *
 * Deliberately NOT {@link SECTION_LABEL}. That token is the tracked-out caps
 * eyebrow that titles a GROUP OF ROWS ("Mode", "Model", "Recommended"); using it
 * on a form field turns every question into a shouted dashboard header and is
 * the single biggest reason a dense form reads as heavy. Fields ask a question —
 * "What's this brief about?" — and questions are written in sentences.
 *
 * Reads one step quieter than the value it labels, so the eye lands on the
 * control rather than the caption.
 *
 * 12px (`text-caption` — 13px on mobile), not 14px. At 14px/500 the label was
 * exactly the size of the 14px/400 value inside the control it names, and a
 * heavier weight than it: the caption out-weighed its own content, which is
 * what "the input text above inputs is massive" describes (founder,
 * 2026-08-26). Three axes now point the same way — size (12 vs 14), weight
 * (500 on the smaller size reads as a caption, not a heading), and contrast
 * (/70 vs full).
 *
 * ⚠️ ONE step, deliberately. `src/index.css` records the 2026-08-07 revert of
 * a 14px→12px label change as "text and icons are way too tiny" — and names
 * the cause: it was taken together with a 16px→14px ICON step. Value text
 * stays `text-sm` and icons stay `size-4`; only the label moved.
 *
 * A prior comment here justified 14px off a 1x marketer.com capture
 * ("Concepts", y378–390). That band is 12px on a 1x capture, and "Concepts"
 * carries both ascenders and a descender, so it implies a ~12–13px font.
 * `OBSERVED-2026-08-26-authenticated-app-walk.md` independently records 13px
 * for that app's card and row titles.
 */
export const FIELD_LABEL = 'text-caption font-medium text-foreground/70';

/**
 * ⚠️ The `Label` primitive's DEFAULT (`src/components/ui/label.tsx`) is kept
 * byte-equal to {@link FIELD_LABEL}, including the `/70`. Do not "fix" it back
 * to a bare `text-foreground`.
 *
 * It was `text-foreground` until 2026-08-26 — byte-identical to the old
 * `SECTION_TITLE` — so across 175 bare `<Label>` render sites a form label
 * rendered at full section-heading contrast, and a bare `<Label>` and an
 * explicit `FIELD_LABEL` were two different labels. It never surfaced as a
 * visible collision only because those sites sit under a 16px `DialogTitle`;
 * any 14px heading above one reproduced the Concept page's wall immediately.
 * Guarded by `src/components/ui/__tests__/label.default-matches-field-label.test.tsx`.
 */

export const ICON_BUTTON = 'text-muted-foreground/60 hover:text-foreground/80 transition-colors';

/**
 * Quiet secondary text action — the ONE grammar for secondary clickable text
 * ("Skip for now", "Back", "Plan my first week", "See all"-class actions).
 * Pair with `<Button variant="link" size="sm">` to override its default
 * text-primary: quiet links are muted until hovered, never brand-colored.
 * Replaces ad-hoc `text-muted-foreground hover:text-foreground` copies
 * (and the drifted /40, /50 variants on home panels are candidates to
 * migrate here).
 */
export const QUIET_LINK =
  'no-underline text-muted-foreground transition-colors hover:no-underline hover:text-foreground';

/**
 * Compact text action — the button grammar for disclosure and one-step
 * secondary actions ("Show more", caption refinements, AI suggestions).
 * These are buttons, not inline links: they gain contrast on hover and never
 * add an underline or a decorative pill surface.
 */
export const QUIET_ACTION =
  'inline-flex items-center gap-1 rounded-md text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

/**
 * Flow navigation or recovery controls in onboarding. The filled primary
 * action is rendered first, followed by its quiet Back/recovery path. On
 * mobile the two stack full-width (native-app tap targets, not a desktop pill
 * shrunk to fit) — pair the primary Button with `w-full sm:w-auto` at each
 * call site; the quiet Back/link action stays its natural width throughout,
 * it isn't a second competing CTA. At `sm:` and up both return to an inline
 * row, matching desktop's compact button rhythm.
 */
export const ONBOARDING_ACTION_ROW =
  'flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-start';

/**
 * The corner remove-X on a hoverable image tile (uploaded logo, attachment
 * thumbnail, avatar). Owns the shared BEHAVIOUR — 20px circle, hidden until the
 * parent `group` is hovered or the button is focused, plus the focus ring — so
 * the recipe stops being copy-pasted (and drifting) per tile. The caller adds
 * `absolute <position>` and its own surface (`bg-background` ± border), and
 * conventionally hovers to `text-destructive` since removal is destructive.
 * Requires a `group` on the positioned ancestor.
 */
export const CORNER_REMOVE_BUTTON =
  'z-10 inline-flex size-5 items-center justify-center rounded-full opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The register for the NAME OF A NAMED THING inside a card or a row — a brief,
 * a starter, a saved workspace, a connected integration, a teammate. 69 files
 * render it (`grep -rln ITEM_TITLE src/components src/pages | wc -l`), which is
 * why the multiplier below is a MEASUREMENT and not a preference.
 *
 * 🔴 INK 85% → 95%, 2026-08-27. The 85% was calibrated against the OLD
 * `--foreground` (dark `240 4.76% 95.88%` = #F4F4F5, 244). When that token
 * dropped to 89% (#E3E3E4, 227) on the same day to close the "does not feel
 * blended" report, this multiplier kept dimming on top of it and every one of
 * the 69 titles DOUBLE-DIPPED — measured on the rendered glyph, not asserted:
 *
 *   marketer.com item title   13px / 500 / #DBD8D2 (219)  ← the benchmark,
 *                                                            read live at
 *                                                            1440x900 on
 *                                                            /dashboard/briefs
 *   Mora, before the round    14px / 500 / #F4F4F5 (244)   +25 vs benchmark
 *   Mora, 85% on the new base 12px / 500 / #C3C3C6 (195)   -24 vs benchmark
 *   Mora, 95% on the new base 12px / 500 / #D7D7D9 (215)   -4  vs benchmark
 *
 * ⚠️ DO NOT DERIVE THAT LAST NUMBER BY HAND. A fractional ink composites over
 * the surface it sits on — here `--card`, rgb(24,24,27) — but it composites in
 * OKLAB, because that is what Tailwind v4 emits and what `getComputedStyle`
 * returns (`oklab(0.912376 … / 0.95)`). Multiplying the sRGB channels gives
 * 0.95·227 + 0.05·24 = 217; the browser actually paints 215. Two points is
 * inside the noise here, but the habit is not: this whole item exists because
 * somebody compared a class name to a reference hex instead of a rendered
 * pixel. Both numbers above were taken twice — `getComputedStyle` through a
 * 1x1 canvas, and the glyph pixel out of the delivered PNG — and they agree
 * exactly. Method and output:
 * `docs/research/competitors/marketer-com/evidence-2026-08-27/R2-measure-ink.mjs`
 * → `R2-measurements.json`. Pinned in code by
 * `src/lib/__tests__/item-title-ink.guard.test.ts`, which fails if either this
 * multiplier or the `--foreground` it was calibrated against moves alone.
 *
 * Full `text-foreground` renders 227, so 95% still holds the item title one
 * visible step below a KPI value — the whole reason this token is fractional
 * and must stay fractional.
 *
 * SIZE stays 12px deliberately, and it is the one axis still off the
 * benchmark. `text-caption` is 12px against marketer's 13px; the scale has no
 * 13px step outside `--text-sidebar-nav-desktop`, which was invented for the
 * nav label and is already borrowed in two dialogs. Promoting it to a general
 * step is the correct fix and is NOT this change: it would move all 69 titles a
 * size class on a round whose brief was ink.
 */
export const ITEM_TITLE = 'text-caption font-medium text-foreground/95';

/**
 * 🔴 THE FEED-CARD TITLE — the register `/discover`'s article cards use, and
 * the one the founder pointed at as correct.
 *
 * Founder, 2026-09-08: _"the article cards have the proper hierarchy and text
 * font usage you should be using in the Respire design system and throughout
 * the page too on items."_
 *
 * Lifted verbatim from `ArticleCard`'s own `<h2>` so the two cannot drift:
 * 14px, medium, `leading-snug`, `tracking-tight`, FULL `text-foreground`.
 *
 * WHEN TO USE WHICH, because this sits one step above `ITEM_TITLE` and the
 * difference is not decorative:
 *
 *   - `CARD_TITLE` — the title is the PRIMARY thing in its row or card and the
 *     reader is deciding something about it (an article to open, a proposal to
 *     approve). It gets full foreground and 14px because it is what the eye is
 *     supposed to land on.
 *   - `ITEM_TITLE` (12px, `text-foreground/95`) — the name of a thing in a
 *     dense list where the row is context rather than the decision: a branch
 *     under a pillar, a source file, a settings row.
 *
 * Pair with `line-clamp-2`, never `truncate`, wherever the reader is deciding
 * from the title: a title they cannot finish reading is a decision they
 * cannot make.
 */
export const CARD_TITLE = 'text-sm font-medium leading-snug tracking-tight text-foreground';

export const ITEM_META = 'text-detail text-muted-foreground';

/**
 * The 13px floor for decision text (a state word, a class word, an action label; DESIGN-PRINCIPLES
 * section 11). The type scale has no 13px step, and `text-caption` is 12px (13px only on a phone),
 * so one constant carries it. `text-[13px]` is a font size to tailwind-merge, so it replaces
 * `text-detail` and `text-caption` when passed through `cn` after them.
 */
export const DECISION_TEXT = 'text-[13px] leading-5';

/**
 * The Home composer text: 16px (chair ruling HOME-TYPE), which also stops the iOS focus zoom.
 * One constant for the field and its placeholder overlay so both share one line height.
 */
export const COMPOSER_TEXT = 'text-base leading-6';

export const META_SEPARATOR = 'text-muted-foreground/40';

// 28px SQUARE thumbnail — rounded-md (10px radius), NOT rounded-full.
// The "SQUARE" in the name is literal. Files, folders, plans, posts, and
// chats all appear as 28px square containers with a 14px inner icon.
// Avatars that need to be circular should NOT use this token.
export const THUMBNAIL_SQUARE =
  'size-7 rounded-md bg-sidebar flex items-center justify-center shrink-0';

export const ROW_HOVER = 'hover:bg-muted/40 transition-colors duration-150';

/**
 * Enter/exit motion for anchored overlays (popover, dropdown, select, context
 * menu, hover card). Pair with the primitive's own Radix transform-origin var
 * so the surface grows out of its trigger, not its centre.
 *
 * Must be a keyframe ANIMATION, never a CSS transition: Radix Presence holds
 * an unmounting node only while an animation runs, and a node that mounts
 * already `data-state=open` has no "from" state to transition out of. The
 * previous `transition-all … data-[state=closed]:scale-95` therefore played
 * neither the open nor the close.
 *
 * Timing follows transitions.dev's menu-dropdown recipe: open 250ms from 0.97,
 * close 150ms to 0.99 (closes are quicker and quieter than opens), on Mora's
 * surface ease. Reduced motion is handled centrally in `src/index.css`.
 */
export const OVERLAY_POP_MOTION =
  'ease-fluid data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-97 data-[state=open]:duration-250 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-99 data-[state=closed]:duration-150';

/**
 * `OVERLAY_POP_MOTION` for Base UI popups (combobox, autocomplete). Base UI marks state with
 * `data-open` / `data-closed`, never `data-state`, so the Radix selectors above match nothing on a
 * Base UI node and the popup snaps open and shut.
 */
export const BASE_UI_OVERLAY_POP_MOTION =
  'ease-fluid data-open:animate-in data-open:fade-in-0 data-open:zoom-in-97 data-open:duration-250 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-99 data-closed:duration-150';

/**
 * Enter/exit motion for a centred modal (Dialog, AlertDialog, full-size wizards). Same open 250ms /
 * close 150ms asymmetry as anchored overlays, but from 0.96: a larger surface starts further away.
 */
export const MODAL_POP_MOTION =
  'ease-fluid data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-96 data-[state=open]:duration-250 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-96 data-[state=closed]:duration-150';

/**
 * Motion `transition` for the nth item of a list that fades up as it mounts: 40ms per item, capped
 * at 8 items so a long list never leaves its last card waiting, on Mora's surface ease. Pair with
 * `initial={{ opacity: 0, y: 8 }}`.
 */
export function listItemEnterTransition(index: number) {
  return { duration: 0.25, ease: [0.3, 0, 0, 1] as const, delay: Math.min(index, 8) * 0.04 };
}

/** Backdrop for a `MODAL_POP_MOTION` surface, timed to finish with it. */
export const MODAL_SCRIM_MOTION =
  'ease-fluid data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-250 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-150';

/**
 * Height easing for a `CollapsibleContent`: opens and closes over the content's measured height
 * (`--radix-collapsible-content-height`, supplied by Radix) instead of appearing and vanishing.
 * `overflow-hidden` is what lets the height clip the content while it animates. Reduced motion is
 * handled centrally in `src/index.css`.
 */
export const COLLAPSIBLE_MOTION =
  'overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up';

/**
 * The pop-in of a panel that appears where you are working (the selection toolbar's proposal): the
 * open half of `OVERLAY_POP_MOTION`, for a panel that is mounted rather than driven by Radix state.
 */
export const PANEL_POP_IN_MOTION = 'animate-in fade-in-0 zoom-in-97 duration-250 ease-fluid';

/** A group label in a picker panel: sentence case, 13px, quiet. */
export const PANEL_GROUP_LABEL = 'text-[13px] leading-5 font-medium text-muted-foreground';

/**
 * New campaign panel motion (transitions.dev scale). Text swap is a 150ms fade; the skeleton-to-content
 * reveal is 400ms; a row's hover colour is 150ms in and out. Only opacity and colour animate, and
 * reduced motion removes the fades.
 */
export const PANEL_TEXT_SWAP_MOTION =
  'animate-in fade-in-0 duration-150 ease-fluid motion-reduce:animate-none';
export const PANEL_REVEAL_MOTION =
  'animate-in fade-in-0 duration-400 ease-fluid motion-reduce:animate-none';
export const PANEL_ROW_HOVER_MOTION =
  'transition-colors duration-150 motion-reduce:transition-none';

/** Quiet structural band for dense operational tables with sortable metrics. */
export const TABLE_HEADER_SURFACE = 'bg-muted/20';

/** Compact operational head; responsive utilities explicitly defeat TableHead's `md:h-12 md:px-4`. */
export const TABLE_HEAD_CELL = 'h-10 px-3 md:h-10 md:px-3';

/** Compact default inset for every shared shadcn data-table body cell. */
export const TABLE_BODY_CELL = 'px-3 py-2 md:px-3 md:py-2';

/** Identity rows carry a 32px tile plus two compact text lines. */
export const TABLE_IDENTITY_ROW = 'h-11';

/** Explicit responsive inset; without `md:py-1.5`, TableCell's `md:p-4` expands rows to ~64px. */
export const TABLE_IDENTITY_CELL = 'px-3 py-1.5 md:px-3 md:py-1.5';

/**
 * TEXT for a plain data cell. The `TABLE_*_CELL` tokens above are PADDING only,
 * and that gap is a real, shipped defect rather than a theoretical one.
 *
 * A `<TableCell>` with bare content inherits shadcn's `text-sm` — 14px, weight
 * 400, FULL foreground. `/campaigns` never shows that, because every cell there
 * wraps its content in an explicit span (`font-medium text-foreground/90` for a
 * name, `text-detail text-muted-foreground` for a secondary). Reach's tables
 * imported the padding tokens and wrote bare content, so `Manual`, `3`,
 * `Not used yet` and `Aug 25` all rendered at the same 14px full-foreground
 * weight as the row's own title — measured on all four Reach tables, 2026-08-25.
 * The founder's words: "like a million different text font sizes and boldness…
 * we want it clean and elegant and matching other tables like the campaigns."
 *
 * Nothing here is new design. It is the campaigns grammar, named so a cell can
 * no longer forget it.
 */

/** A first-class value in a data cell — a count, a name you can read at a glance. */
export const TABLE_CELL_VALUE = 'text-caption text-foreground/85';

/** A secondary or derived value — a type, a date, a status phrase. Most cells. */
export const TABLE_CELL_MUTED = 'text-caption text-muted-foreground';

/** A numeric cell. Right-aligned and tabular so digits align down the column. */
export const TABLE_CELL_NUMERIC = 'text-caption tabular-nums text-foreground/85 text-right';

/**
 * THE frame every data table wears. Applied by `<Table>` itself — call sites do
 * not opt in, they opt OUT with `unframed`.
 *
 * The rule it encodes (decided 2026-08-23, `docs/planning/PLAN-TABLES-AND-PUBLISHING-IA-2026-08-23.md`):
 * **a table that IS the page needs no frame; a table that is one object on a
 * page which also has a header, a toolbar, notices and other sections needs an
 * edge, because everything around it already has one.**
 *
 * That single criterion is what separates the references rather than taste:
 * Linear's issue list and Notion's database view are the whole content area, so
 * the viewport is their frame and they carry none. shadcn's own data-table demo,
 * Shopify Polaris (`IndexTable` inside a `Card`), Stripe, Spreadjam and GA4 are
 * all one block on a page, and all carry a rounded border. marketer.com carries
 * a border AND elevation — the founder rejected the elevation specifically, not
 * the border, which is why there is no shadow here.
 *
 * Values match `PANEL_CONTAINER` so a framed table and a framed panel read as the
 * same object class. `overflow-hidden` is load-bearing: without it the header
 * band's `bg-muted/20` paints square corners over the rounded frame.
 *
 * Replaces `TABLE_OPERATIONAL_FRAME` (`rounded-t-md`), which rounded the header
 * band's top corners against no border at all — half a frame, which is why the
 * Posts table read as loose text while the Settings team table beside it read as
 * a finished object.
 */
export const TABLE_FRAME = 'rounded-md border border-border/60 overflow-hidden';

/**
 * Desktop-only fused capsule for a table's 1–2 visible secondary actions.
 *
 * ⚠️ `border-border/60` + transparent, NOT `border-sidebar-border/80` +
 * `bg-sidebar`. One toolbar row on `/campaigns` was painting THREE different
 * fills side by side: the search field (transparent), the "Last 30 days"
 * `SelectPill` (Button `outline` → `bg-background`), and this capsule
 * (`bg-sidebar`). Founder, 2026-08-23: _"inconsistencies on the background
 * colors — the thing for Last 30 days has a different background colour than
 * the pills beside it... and Search campaigns which has a different background
 * colour. There's a lot of just inconsistencies here."_
 *
 * A toolbar is ONE control family. They share a border token and a
 * transparent ground; what distinguishes them is shape (field / pill /
 * capsule), not fill.
 */
export const TABLE_DESKTOP_ACTION_GROUP =
  // Delegates its LOOK to `.table-action-group` (src/index.css) and keeps only
  // the responsive behaviour that is genuinely its own (hidden below `sm`).
  //
  // ⚠️ It pointed at `.page-header-action-group` until 2026-08-23, which made a
  // TABLE control wear the PAGE ACTION shape: a 9999px stadium sitting beside
  // three 10px facet triggers on /campaigns and /discover/ads, with circular
  // children floating inside it. `.table-action-group` is identical in every
  // respect except radius — see the comment on it for why that is a separate
  // class rather than a modifier.
  'hidden shrink-0 items-center sm:inline-flex table-action-group';

// =============================================================================
// Bordered field box — the wrapper for a real text input that must read as
// editable (border + background), not chromeless text. Pair with a flush
// (border-0 bg-transparent) Textarea/Input inside so there's one visible
// edge, not two nested ones. Source of truth: CaptionDisplay.
// =============================================================================
export const TEXT_INPUT_BOX =
  'rounded-lg border border-border bg-card transition-colors focus-within:border-ring/60 focus-within:shadow-sm focus-within:shadow-ring/10';

// =============================================================================
// Selectable pill — the ONE canonical selection toggle (single- or multi-select).
// Source of truth: the Discover preferences selector (FirstVisitModal Pill).
// Use for every chip/toggle the user picks from: category filters, platforms,
// scene/outfit, aspect/format, count/duration. NO radio circles, NO checkmarks,
// NO bg-primary — selection is carried by border-foreground/40 + bg-sidebar.
// =============================================================================
export const SELECTABLE_PILL =
  'inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-detail font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed';
/** Apply when the pill is selected. */
export const SELECTABLE_PILL_ON = 'border-foreground/40 bg-sidebar text-foreground';
/** Apply when the pill is not selected. */
export const SELECTABLE_PILL_OFF =
  'border-border/60 text-muted-foreground hover:border-border hover:bg-muted/40 hover:text-foreground';

// =============================================================================
// Value chip — a short value the workspace HOLDS: a followed subject, a
// competitor, a brand-voice tone, a pillar. ONE filled-neutral grammar, read-only
// (Discover rail) or removable (Settings `ChipListField`), so the same list never
// looks different on two surfaces. NOT a selection toggle (that is
// SELECTABLE_PILL) and NOT uppercase metadata (that is META_PILL).
// =============================================================================
export const VALUE_CHIP =
  'inline-flex h-7 items-center gap-1 rounded-md border border-transparent bg-muted px-2.5 text-xs font-normal text-muted-foreground';
/** Added when a value chip is pressable (opens its editor). */
export const VALUE_CHIP_INTERACTIVE =
  'transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
/** Added when the value has nothing behind it yet (a subject with no stories). */
export const VALUE_CHIP_EMPTY = 'border-dashed border-border bg-transparent text-muted-foreground';
/** The "+ add" / suggestion chip that sits among value chips: same height,
 *  type and radius as VALUE_CHIP so a row of chips reads as one row, dashed
 *  because nothing is there yet. Full-strength muted text — the /70 version
 *  measured ~2.8:1 on light and failed AA (design review, 2026-10-05). */
export const VALUE_CHIP_ADD =
  'inline-flex h-7 items-center gap-1 rounded-md border border-dashed border-border bg-transparent px-2.5 text-xs font-normal text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

// =============================================================================
// Segment chip — ONE-OF-N, ALL OPTIONS VISIBLE, EXACTLY ONE ON.
//
// The single source of truth for how a selected option in a horizontal
// single-select row is drawn, shared by BOTH `SegmentedToggle` (view/mode
// switch, tablist semantics) and `FilterChipRail` (scrolling scope rail with
// counts). Those two components keep their own behaviour — they do genuinely
// different jobs — but they may not keep their own LOOK: on 2026-09-04 the
// founder found them stacked two rows apart on `/reach/approvals` and side by
// side across `/discover` vs `/discover/ads`, drawing what a reader reads as
// one object in two vocabularies (bordered outline pill vs borderless fill).
// _"We need to have one component for these throughout the app… there
// shouldn't be these discrepancies."_ Importing one token from one file is how
// that stops being a thing two files have to remember.
//
// THE GRAMMAR, and what each half means:
//   • Unselected is PLAIN TEXT on the page's own ground. No border, no fill.
//     A border on every option says "each of these is a button"; they are not,
//     they are the states of one control.
//   • Selected is a soft FILL and nothing else — no border, no shadow. Shadow
//     implies a raised chip inside a well, which is the capsule grammar this
//     replaced (see `SegmentedToggle`'s `ground` prop for that history).
//   • COLOUR IS CONTENT, NEVER CHROME. The one licensed accent here is a
//     caller-supplied leading `icon` in its own brand colour (the Meta mark on
//     `/discover/ads` is the reference — the founder named that highlight as
//     the part worth keeping). The chip itself stays neutral in every state,
//     so the accent always means "this is Meta", never "this is on".
//
// NOT this object — do not reach for these tokens for:
//   • A control that opens a menu → `SelectPill` (it keeps its border; the
//     border plus caret is what promises "there is more behind this").
//   • MULTI-select filters where any number can be on → `SELECTABLE_PILL`
//     above, whose per-chip border is what makes four-of-nine-on legible.
//   • Route navigation → the underline tab strip (`UNDERLINE_TAB*`). Underline
//     navigates, fill filters; a page that uses one grammar for both reads as
//     nine peer destinations when it has five and a filter.
// =============================================================================
export const SEGMENT_CHIP =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-colors ' +
  'focus-visible:outline-none';
/** Apply when the option is the selected one. */
export const SEGMENT_CHIP_ON = 'bg-muted text-foreground';
/** Apply to every option that is not selected. */
export const SEGMENT_CHIP_OFF = 'text-muted-foreground hover:bg-muted/60 hover:text-foreground';

// =============================================================================
// Meta pill — non-interactive uppercase metadata badge (source/platform/
// category chip on a content card, e.g. ArticleCard's domain badge or
// BrandPostCard's platform badge). NOT selectable (that's SELECTABLE_PILL
// above), NOT a status indicator, NOT overlay-only chrome — those get their
// own treatment. Positioning (`absolute`) and effect extras (`backdrop-blur-sm`)
// stay inline at the call site via `cn()`; this token is the shared recipe
// only. Source of truth: Discover card badges (ArticleCard, BrandPostCard,
// TrendingTopicCard).
// =============================================================================
export const META_PILL =
  'inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/80 px-1.5 py-0.5 text-micro font-medium uppercase tracking-wider text-foreground/70';

/**
 * Read-only tags inside dense tables are values, not conversational chips.
 * Keep the shared meta treatment, but square its corners to align with the
 * grid's controls and status fields.
 */
export const TABLE_TAG_CHIP = `${META_PILL} rounded-sm`;

/**
 * The ONE next-step chip in the chat thread — the turn footer's chip rail
 * (CompletionCard) and the provide_answer / prompt-link rails that stand in for
 * it on non-generation turns. They render the same job, so they share one class.
 *
 * Before this there were three: h-7/rounded-lg/border-40 in CompletionCard,
 * h-8/rounded-full/border-60 in AgentMessagePart (x3 sites), and
 * ACTION_PILL_CLASS in the media-studio rail. Two of the three had NO
 * focus-visible ring, so the primary next-step surface was invisible to
 * keyboard users.
 *
 * Geometry matches ACTION_PILL_CLASS (h-8, rounded-full, px-3.5) so chat and
 * dialog read as one family; the media-studio rail keeps its own class because
 * it lives in a horizontally-scrolling row (shrink-0, scroll-margin) that a
 * wrapping thread rail must not inherit.
 */
export const TURN_FOOTER_CHIP =
  'inline-flex h-8 items-center gap-1.5 rounded-full border border-border/60 bg-transparent px-3.5 text-caption font-normal text-foreground/80 transition hover:bg-muted/30 hover:text-foreground hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

/**
 * THE SOURCE-REMEDY REGISTER — the ONE way this app prints "this source has
 * stopped reporting, and here is the fix".
 *
 * A word, in the semantic `warning` token, under a dashed underline. It is
 * shared rather than described because describing it is exactly what failed:
 * `FreshnessChip` carried this string privately, the Insights scope menu
 * hand-rolled a plain `text-foreground/80` span for the same word, and the
 * result shipped on 2026-08-27 — measured live at 1440x900 with
 * `getComputedStyle` — as FOUR controls in ONE popover reading the single word
 * `Reconnect` in two different registers, 64px apart:
 *
 *   Facebook  y=206  rgb(179,83,9)          underline dashed   ← FreshnessChip
 *   LinkedIn  y=238  rgb(179,83,9)          underline dashed   ← FreshnessChip
 *   Shopify   y=302  oklab(0.259 … / 0.8)   none      solid    ← hand-rolled
 *   Google An y=334  oklab(0.259 … / 0.8)   none      solid    ← hand-rolled
 *
 * Both pairs dispatched the same hand-off to the same destination. The plain
 * pair carried no affordance signal at all — right-aligned status text that
 * happened to be clickable.
 *
 * 🔴 IT IS FOR `Reconnect`, NOT FOR `Connect`. A source that was reporting and
 * stopped is a DEGRADATION and earns the warning token. A source that was
 * never connected is an ABSENCE — see `FreshnessChip`'s `never-connected`
 * branch, which refuses to badge one for exactly this reason. Painting
 * `Connect` amber puts a permanent deficiency marker on a channel the account
 * simply does not use. Use {@link SOURCE_INVITE} for that word.
 *
 * Padding is deliberately NOT baked in: the chip sits inline beside a card
 * title in some places and as a standalone row control in others, and a
 * baked-in box would move every analytics card header. Pass hit-area padding
 * at the call site.
 */
export const SOURCE_REMEDY =
  'shrink-0 text-detail font-medium text-warning underline decoration-dashed decoration-warning/50 underline-offset-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';

/**
 * The counterpart word for a source that was never connected — `Connect`. Same
 * geometry and weight as {@link SOURCE_REMEDY} so the two read as one column,
 * deliberately NOT the warning token so an unused channel is never dressed as
 * a broken one.
 */
export const SOURCE_INVITE =
  'shrink-0 text-detail font-medium text-foreground/80 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';

/** Standard height for attachment / reference chips in the composer. */
export const CHIP_HEIGHT = 'h-7';

/** Hover fill for a whole interactive chip (clickable container). */
export const CHIP_INTERACTIVE_HOVER = 'hover:bg-muted/20 transition-colors';

// =============================================================================
// Settings dialog shared patterns (used by every page in SettingsDialog).
// Source of truth for ALL settings pages — never re-declare locally.
// =============================================================================

/**
 * The QUIET SECOND-LEVEL heading — a group nested inside something that already
 * has a {@link SECTION_TITLE} above it. Sentence case, NEVER `font-semibold`
 * (that is reserved for `text-2xl`+ page headers). Grouping is carried by the
 * panel boundary (`PANEL_CONTAINER_INSET` + `PANEL_DIVIDER_INSET`), not by
 * weight. In settings, use via the `SectionHeader` primitive, not by hand.
 *
 * Despite the name it is NOT settings-only: `AuthoringSection` uses it for the
 * groups inside an authoring step, whose step header is the SECTION_TITLE above
 * them. The name is kept because `SectionHeader`/`SubsectionHeader` in
 * `src/components/settings/primitives/` are documented against it.
 *
 * ⚠️ Only correct above a 12px {@link SETTINGS_FIELD_LABEL}. Placed above a
 * 14px/500 {@link FIELD_LABEL} it renders LIGHTER than the fields it heads and
 * the hierarchy inverts — measured on `/briefs/:id/concepts/:id`, 2026-08-26.
 */
export const SETTINGS_SECTION_HEADING = 'text-sm font-normal text-foreground/85';

/** Description sentence directly under a SETTINGS_SECTION_HEADING (11px descender). */
export const SETTINGS_SECTION_DESCRIPTION = 'text-detail text-muted-foreground';

/**
 * The grouped-rows CARD that settings rows live inside.
 *
 * Founder, 2026-08-25, pointing at the style guide's Color-tokens list: "that's
 * kind of a table with rows inside of a little table that looks really nice. I
 * love that." Settings pages were rendering bare `divide-y` row stacks with no
 * container, so a lone toggle floated in whitespace with nothing grouping it.
 *
 * The rows themselves are unchanged — `SETTINGS_ROW` still carries no
 * horizontal padding, and this group supplies it (`[&>*]:px-4`) so a row is
 * identical whether it sits in a group or not. Do NOT add `px-*` to a row to
 * "make it line up"; that double-pads it here.
 *
 * Use via the `SettingsGroup` primitive, never by hand — the style guide's
 * own list hand-rolls this shape and is the reason it kept getting copied.
 */
export const SETTINGS_GROUP =
  'divide-y divide-border/40 overflow-hidden rounded-lg border border-border/60 bg-card [&>*]:px-4';

/** Standard non-interactive row (e.g. a Switch row). py-3, no horizontal padding. */
export const SETTINGS_ROW = 'flex items-center gap-3 py-3';

/** Inline icon to the left of a settings row. NEVER wrap in a colored square. */
export const SETTINGS_ROW_ICON = 'size-4 shrink-0 text-muted-foreground/70';

/** Title of a settings row (e.g. "Dark mode", "Auto-categorize comments") */
export const SETTINGS_ROW_TITLE = 'text-caption font-medium text-foreground/85';

/** Description sentence under SETTINGS_ROW_TITLE */
export const SETTINGS_ROW_DESCRIPTION = 'text-detail text-muted-foreground';

/** Hairline divider between settings rows */
export const SETTINGS_DIVIDER = 'h-px bg-border/40';

/**
 * Provider identity tile in the Integrations directory/detail. The neutral
 * group belongs to Mora; the brand mark inside it stays provider-native.
 */
export const INTEGRATION_ICON_TILE =
  'relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-background';

/**
 * Flat provider destination in Settings → Integrations. One row, one nested
 * destination: no card-grid chrome, divider, caret, or inline overflow menu.
 */
export const INTEGRATION_DIRECTORY_ROW =
  'group flex min-h-14 w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-55';

/** Responsive provider directory: roomy two-column scan on desktop, one column on touch widths. */
export const INTEGRATION_DIRECTORY_GRID = 'grid grid-cols-1 gap-1 lg:grid-cols-2';

/**
 * Trailing mark on a CONNECTED row: a quiet green check, never a disc.
 *
 * Founder, 2026-08-22: "I hate the way that looks whenever it's an active
 * connection. That icon group... it should just be a check mark." The disc is
 * still unnecessary chrome around a mark that never changes, and it would make
 * a settled state shout louder than the "+" you are actually meant to click.
 * A restrained success tint now makes the settled connection legible without
 * turning the mark into a badge or a second call to action.
 *
 * Do NOT reuse this for the "+" on an available row — see
 * `INTEGRATION_CONNECT_AFFORDANCE`. The two used to share one token under the
 * comment "matching circular trailing control for connect and connected
 * states", and matching them was the bug: a status is permanent, an affordance
 * appears when you reach for it.
 */
export const INTEGRATION_STATE_CONTROL =
  'flex size-8 shrink-0 items-center justify-center text-success/80';

/**
 * Trailing "+" on an AVAILABLE row. Bare glyph at rest; the circle appears
 * only when the pointer is on the "+" ITSELF — `hover:`, never `group-hover:`.
 *
 * Founder, 2026-08-22, against the ChatGPT Plugins list: "the plus sign
 * shouldn't have a group around it. The only time it should have a group is
 * when that plus sign is hovered." Row hover tints the ROW; it must not also
 * light up the control, or every row advertises a pressed-looking button you
 * are not pointing at. Because the "+" is nested inside the row, pointing at
 * it correctly shows both — the row tint AND the circle — which is exactly
 * what the reference screenshot shows.
 */
export const INTEGRATION_CONNECT_AFFORDANCE =
  'flex size-8 shrink-0 items-center justify-center rounded-full text-foreground/60 transition-colors hover:bg-muted hover:text-foreground';

/** Quiet announcement badge shared with the app sidebar's NEW treatment. */
export const ANNOUNCEMENT_PILL =
  'inline-flex h-4 shrink-0 items-center rounded-full border border-border bg-sidebar px-1.5 text-micro font-medium uppercase leading-none tracking-wide text-muted-foreground/60 group-data-[collapsible=icon]:hidden';

/** Footer row for Save/Cancel buttons */
export const SETTINGS_FOOTER = 'mt-6 flex justify-end gap-2 border-t border-border/40 pt-4';

/**
 * The foot of a decision dialog (Cancel and the one primary action). The dialog primitive scrolls as one
 * block when its content is taller than the screen, which carried the buttons off the bottom edge at 390
 * wide. Pinned to the dialog's bottom edge it stays in view while the body scrolls; the negative margins
 * cancel the primitive's p-6 and -bottom-6 sticks the bar to the dialog's outer edge (not the padded
 * content edge), so the bar runs edge to edge and nothing shows beneath it.
 */
export const DIALOG_PINNED_FOOTER =
  'sticky -bottom-6 z-10 -mx-6 -mb-6 border-t border-border/40 bg-background px-6 py-4';

/** Shared Settings dialog horizontal breathing room across every section. */
export const SETTINGS_DIALOG_GUTTER = 'px-4 md:px-8';

/**
 * The one readable content measure for Settings. Individual settings pages
 * should not choose their own desktop width: the dialog owns the measure so
 * a change of section never feels like a change of product. Tables may scroll
 * horizontally inside this column rather than making the whole page wider.
 */
export const SETTINGS_DIALOG_CONTENT =
  'animate-in fade-in duration-200 mx-auto w-full max-w-2xl pb-16';

/** Compact dashed `+ Add` chip used as empty-state CTA */
export const SETTINGS_DASHED_CHIP =
  'inline-flex items-center gap-1.5 rounded-full border border-dashed border-border/60 bg-transparent px-3 py-1 text-detail font-normal text-muted-foreground/70 transition-colors hover:border-border hover:bg-muted/40 hover:text-foreground/85';

/** Form field label (above an Input/Select/etc.) */
export const SETTINGS_FIELD_LABEL = 'text-xs font-medium text-muted-foreground';

// =============================================================================
// Icon-group container. Pair with <CircleIconButton /> from
// '@/components/ui/circle-icon-button' for trailing card actions, toolbar
// clusters, and dialog-header button rows. NEVER re-declare locally.
// =============================================================================

export const ICON_GROUP_CONTAINER = 'flex shrink-0 items-center gap-1';

// =============================================================================
// Segmented nav group — a persistent (all-breakpoint) rounded-full bordered
// pill fusing 2-3 tightly related controls (‹ Today › style date nav, rail
// scroll arrows) into one read as a single built object, matching the
// bordered-container weight Insights' `AnalyticsFiltersPopover` trigger
// already carries via `Button variant="outline"`. Lifted out of
// StudioPage's curated-avatars rail arrows (`!rounded-l-full
// !rounded-r-none` / `!rounded-l-none !rounded-r-full` on the end members,
// divided by a hairline) so that markup has one definition instead of being
// hand-copied per call site. Pair each end member with
// `variant="flat" size="toolbar-icon"` (or `size="sm"` for a labeled middle
// button); the container supplies the border, members stay borderless.
// =============================================================================

export const SEGMENTED_GROUP = 'inline-flex items-center rounded-full border border-border/60';
export const SEGMENTED_GROUP_ITEM_START = '!rounded-l-full !rounded-r-none';
export const SEGMENTED_GROUP_ITEM_END = '!rounded-l-none !rounded-r-full';
export const SEGMENTED_GROUP_ITEM_MIDDLE = '!rounded-none';
export const SEGMENTED_GROUP_DIVIDER = 'h-3.5 w-px shrink-0 bg-border/60';

// =============================================================================
// PROGRESS COMPLETION BANDS — the one place a completion fraction becomes a
// colour, so `Progress`, `SegmentMeter` and anything else that draws a
// "N of M done" mark cannot drift into three different ramps.
//
// The founder's ask, verbatim: progress bars "should fill up with one of our
// primary colours or our green colour from the insights page… they would go in
// sets of twenty percent or something, filling up with colour that gets more
// intense as the concepts fill up."
//
// Five bands, consumed TWO WAYS depending on whether the mark has positions:
//   · a CONTINUOUS bar (`Progress`) takes one band for its whole fill, chosen
//     by the total fraction — `progressBandClass`. Deliberately NOT a gradient
//     down its own length, which would encode the same magnitude twice inside
//     one shape and leave the reader estimating where the colour changed.
//   · a SEGMENTED meter (`SegmentMeter`) ramps ACROSS ITS FILLED MARKS, the
//     first lightest and the last deepest — `progressSegmentClass`. Here the
//     positions already exist, so the ramp adds direction, not a second copy
//     of the length. See that function's docblock for why it spans the FILL
//     rather than the five absolute positions, and what that costs; the
//     founder asked for this one by name ("goes more bold slightly from 0 to 5
//     left to right").
//
// 🔴 WHY REAL TOKENS AND NOT OPACITY. An opacity ladder down `--chart-1` is
// the obvious implementation and it is already a measured failure in this
// repo: `RankedBars` shipped `[1, .74, .58, .46, .38]` and the bottom step
// measured 1.57:1 light / 2.06:1 dark against the surface behind it. The bands
// are five real per-theme colours in `index.css`, and their direction INVERTS
// between themes — light deepens (Blue 500 → Blue 700) against a near-white
// `--muted` track, dark brightens (Blue 600 → Blue 400) against a near-black
// one. A single ramp cannot serve both grounds.
//
// The terminal state is `--success`, not a sixth blue: "done" is a different
// statement from "nearly done", and green is the token that already makes it
// everywhere else in the app.
//
// ⚠️ THE TERMINAL GREEN is the part reserved for completion, NOT the blue
// ramp. Corrected 2026-08-27: this note used to forbid spending the whole
// scale on "a magnitude, a quota, or a count", and under that reading the
// Brief card's `Concepts` row was left flat grey — the grey the founder was
// looking at when he asked for colour. A blue ramp on a count is an ordered
// sequential scale saying "there are three of these", which is honest. What
// would be a lie is a "storage used" bar going GREEN at 100%, because green
// claims a good end state that a magnitude never reaches. So: the ramp is
// available to any ordered quantity; `--success` is available only to a real
// completion. Call sites that mean neither pass their own
// `indicatorClassName` / tone and keep it.
// =============================================================================

/** The five band classes, low → high. Written as literals so Tailwind's
 *  scanner sees them; a constructed `bg-progress-${n}` would be pruned. */
const PROGRESS_BANDS = [
  'bg-progress-1',
  'bg-progress-2',
  'bg-progress-3',
  'bg-progress-4',
  'bg-progress-5',
] as const;

/** Terminal band: complete. */
export const PROGRESS_COMPLETE = 'bg-success';

/**
 * Fraction (0–1) → the background class for a completion mark.
 *
 * Bands are 20% wide and CLOSED at the top: 0.2 is still band 1, 0.21 is band
 * 2, and only a true 1 reaches `--success`. A fraction at or below zero
 * returns band 1 — the mark has no width at 0, so the colour never shows, and
 * returning a class keeps every caller's type simple.
 *
 * `NaN` (an unknown numerator or a zero denominator) is band 1 for the same
 * reason: an unknown is not a completion and must never render as one.
 */
export function progressBandClass(fraction: number): string {
  if (!Number.isFinite(fraction)) return PROGRESS_BANDS[0];
  if (fraction >= 1) return PROGRESS_COMPLETE;
  if (fraction <= 0) return PROGRESS_BANDS[0];
  const band = Math.ceil(fraction * PROGRESS_BANDS.length);
  return PROGRESS_BANDS[Math.min(band, PROGRESS_BANDS.length) - 1];
}

/** How many bands the ramp has — the segment count a discrete meter must draw
 *  if every band is to be reachable. Exported so `SegmentMeter` cannot drift to
 *  four or six segments and silently strand a colour. */
export const PROGRESS_BAND_COUNT = PROGRESS_BANDS.length;

/**
 * FILLED-SPAN POSITION → band, for a meter drawn as discrete marks.
 *
 * WHY IT DIFFERS FROM `progressBandClass`. A continuous bar has no positions —
 * it is one mark whose LENGTH is the datum, so colouring its own length as a
 * gradient would encode one magnitude twice inside one shape. Its whole fill
 * takes ONE band. A segmented meter already has marks at known positions, so a
 * ramp across them is not a second encoding: it is what lets the eye read the
 * row left-to-right the way the founder asked for it — "goes more bold
 * slightly from 0 to 5 left to right".
 *
 * 🔴 THE RAMP SPANS THE FILLED MARKS, NOT THE FIVE ABSOLUTE POSITIONS — and
 * this is a deliberate reversal, decided 2026-08-27 with the cost stated.
 *
 * Under absolute positions, `n` filled marks only ever spent the first `n`
 * bands. Measured live at 1440x900: at n=2 the two marks were rgb(58,125,233)
 * and rgb(33,111,237) — 1.16:1 apart on a 12x4px mark, indistinguishable; at
 * n=1 there was nothing to ramp at all. Read-only SQL on the test account the
 * same day: exactly ONE Brief of eighteen reaches 5 Concepts. So the ramp was
 * invisible on seventeen cards out of eighteen.
 *
 * Spanning the fill instead — mark `i` of `n` takes band `round(i·4/(n−1))` —
 * puts band 1 at the first filled mark and band 5 at the last on EVERY row.
 * At n=5 the two models are identical, so nothing is lost where the old one
 * worked.
 *
 * ⚠️ THE COST, stated rather than buried: the deepest hue now marks "the LAST
 * one", not "the fifth one". Hue therefore no longer carries the absolute
 * count — the mark COUNT against the visible track and the numeral beside it
 * do, and they carry it exactly, which hue at 1.16:1 never did. `Concepts 2`
 * and `Concepts 5` both end deep; they differ by two marks versus five.
 *
 * A single filled mark takes the deepest band: it is the frontier, and the
 * palest band on one lonely mark would sit barely above the track.
 *
 * ⚠️ A RAMP IS NOT A COMPLETION. It says "three of these exist", never "you
 * are 60% done". Only `SegmentMeter`'s `completion` tone adds the terminal
 * `--success`, and only when the denominator is actually reached.
 *
 * ACCESSIBILITY: LIGHTNESS climbs monotonically across the five tokens in BOTH
 * themes (light deepens 57%→40%, dark brightens 54%→71% — opposite directions,
 * same monotonic step, because the tracks are opposite grounds), so the ramp
 * survives greyscale. Asserted against `src/index.css` itself by
 * `progress-bands.test.tsx`. Never reorder `PROGRESS_BANDS`.
 */
export function progressSegmentClass(index: number, filledCount: number): string {
  const last = PROGRESS_BANDS.length - 1;
  if (!Number.isFinite(index) || !Number.isFinite(filledCount)) return PROGRESS_BANDS[last];
  // A meter never draws more marks than it has bands, so a fill of 8 in a
  // five-mark meter still spends the whole ramp rather than bunching in the
  // first two bands.
  const drawn = Math.min(Math.floor(filledCount), PROGRESS_BANDS.length);
  const span = drawn - 1;
  if (span <= 0) return PROGRESS_BANDS[last];
  const position = Math.min(Math.max(Math.floor(index), 0), span);
  return PROGRESS_BANDS[Math.round((position * last) / span)];
}

// =============================================================================
// Analytics card surface. ONE chrome + inner-padding rhythm for every KPI tile,
// chart, breakdown table, and demographics card so they read as a single
// surface and the padding lives in one place (was drifting per-card with heavy
// default p-6 that wasted space on mobile). Flat, soft border, tinted fill.
// =============================================================================

export const ANALYTICS_CARD = 'rounded-xl border-border/60 bg-card/60 shadow-none';
// CardHeader override (card has a titled header above the body).
export const ANALYTICS_CARD_HEADER = 'px-4 pt-4 pb-2';
// CardContent override beneath a header.
export const ANALYTICS_CARD_BODY = 'px-4 pb-4 pt-0';
// CardContent override for a self-contained card with no header (e.g. KPI tile).
export const ANALYTICS_CARD_BODY_FULL = 'p-4';

// =============================================================================
// DEAD BOTTOM SPACE — the pair that stops a card ending on a hole.
//
// 🔴 THE DEFECT THESE MEASURE AGAINST. `Card` is a plain block div and
// `CardContent` is a plain block child, so a card handed extra height by a CSS
// grid (a `stretch` row whose tallest sibling sets the height, or a `min-h-*`
// floor at the call site) puts ALL of that extra height under its content. The
// card then ends on a void with no line in it.
//
// Measured live on `/insights/overview`, 1440x900, `test@example.com`,
// 2026-08-27 — px between the last text box and the card's bottom edge, where
// the healthy datum is `pb-4` ≈ 17px:
//
//   Where it's coming from      240px tall   174px dead   (stretched by a
//                                                          240px empty chart)
//   You vs competitors          288px tall   115px dead   (stretched by
//                                                          Best time to post)
//   Best post per platform      192px tall    75px dead   (a min-h-48 floor)
//   every other card on the page             13-26px      OK
//
// Founder, 2026-08-27: "the cards are just, the cards need to fill the space
// I suppose."
//
// -- THE FIX IS RULE 22, NOT A RAGGED ROW ------------------------------------
// The tempting fix is `items-start` on the row so every card sizes to its own
// content. `InsightsOverviewTab` already rejected that in writing for the
// charts row - sibling data cards whose natural heights differ by ~45px read
// as sloppy - and design hard rule 22 gives the other answer: content that is
// shorter than its container gets CENTRED in it, never top-anchored with the
// slack dumped at the bottom.
//
// So: `CARD_FILLS_ITS_ROW` on the `Card` makes it a flex column (a plain block
// cannot pass height down at all), and `BODY_CENTERS_IN_SPARE_HEIGHT` on its
// `CardContent` claims that height and centres what is in it. Both are no-ops
// when the card's content already fills it, which is the common case - this
// pair can only ever move slack, never create it.
//
// ⚠️ NOT for a card whose body must stay top-aligned against a sibling's rows
// (a table beside a table, where centring would break the shared baseline).
// =============================================================================

export const CARD_FILLS_ITS_ROW = 'flex flex-col';
export const BODY_CENTERS_IN_SPARE_HEIGHT = 'flex flex-1 flex-col justify-center';

// =============================================================================
// THE ANALYTICS CARD TYPE REGISTERS — four rungs, and the whole /insights page
// runs on these and nothing else.
//
// Why the card title got QUIETER (2026-08-27). Every analytics card shipped
// `text-sm font-medium` — 14px/500 near-foreground — which made the card's own
// NAME the largest, brightest ink inside it while the largest datum beside it
// was 12px/500. Measured on /insights the same day: on 7 of 13 cards the
// loudest thing was the card's name, not its number. That is the founder's
// literal sentence — "so much white text popping out at you… way too much
// emphasis on certain things and not on others" — and it is the 0-anchor of
// the label/value criterion in `29-DESIGN-BASIS.md` §4 C5.
//
// 11px/400/muted is not invented. It is the register `SortableTableHead`
// already ships for a column header, and the rule was already written down in
// `references/data-tables.md` §2 — "A 14px near-foreground header competes with
// the row content it labels" — and enforced on tables while never being
// applied to cards. Guarded by `analytics-card-title.register.test.ts`.
//
// ⚠️ ONE DELIBERATE DIVERGENCE from that table header, recorded so nobody
// "fixes" it: the table header is `font-medium`, this is `font-normal`. A card
// title sits directly above a 12px/500 datum, so at `font-medium` the
// title→datum adjacency separates on only 2 axes (size, colour) and one of
// them is a 1px size step. `font-normal` makes it 3 (size, weight, colour),
// which is the bar C5 sets. A table header has no such neighbour — its values
// are a whole cell away — so it keeps its weight. Size and colour are shared;
// the guard test asserts exactly that, and asserts the weights differ.
// =============================================================================

/**
 * Rung 3 — LABEL. The card title, every category/row label inside the card,
 * and every axis tick. All three at the same quiet register on purpose: when
 * the row label and the card name are equally quiet, the only loud things left
 * on the card are the numbers.
 */
export const ANALYTICS_CARD_TITLE = 'text-detail font-normal text-muted-foreground';

/**
 * Rung 2 — DATUM. A number printed ON a mark (a bar, a pill, a map region) or
 * beside a row label. `tabular-nums` so a column of them does not jitter.
 *
 * 🔴 MUTED, NOT `text-foreground` — corrected 2026-08-28,
 * `docs/planning/mora-growth-intelligence-overhaul-2026-08-26/
 * 50-INSIGHTS-VISUAL-DECISIONS.md` Decision 2(b). `text-foreground` measured
 * **11.94:1 light / 13.69:1 dark** against `--card` — roughly 2.2–2.5× the
 * register `46-CARD-ARCHETYPES.md` §4 A4.1 already specifies for this exact
 * rung ("Value and share are `#a49e97`, not `#dbd8d2`" — `text-muted-
 * foreground` measures **4.83:1 light / 6.91:1 dark**), and §1.4's TYPE RULE
 * point 3 ("the value is the darkest ink on the card, and nothing else on the
 * card is at value weight and value colour simultaneously") names the KPI
 * headline (`STAT_VALUE`), not this rung, as the one place that gets to be
 * full-strength ink. `font-medium` is unchanged — weight was never the
 * defect; marketer's own on-mark values are also 500. **Do NOT mute
 * `STAT_VALUE`** (the KPI hero number) — that token is deliberately still
 * `text-foreground` and stays that way.
 */
export const METRIC_ON_MARK = 'text-caption font-medium tabular-nums text-muted-foreground';

/**
 * Rung 4 — FOOTER. The quiet line under a card body carrying facts ABOUT the
 * number: its window, its coverage, its evidence class, its denominator. This
 * is where the deleted card subtext's *facts* live; its *explanations* move
 * into the title's `definition` tooltip instead. Never a second sentence of
 * marketing about what the card is.
 *
 * 🔴 `text-muted-foreground`, NOT `text-muted-foreground/70`, and the alpha was
 * measured before it was dropped. Composited against the card fill on
 * `/insights` 2026-08-27, the 70% form renders **2.73:1 in light** and
 * **4.13:1 in dark** — under the 4.5:1 AA floor for text, and in light under
 * even the 3:1 non-text floor. `29-DESIGN-BASIS.md` P16 is explicit that the
 * fourth text tier "is for chrome icons, placeholders and disabled arrows only,
 * never for a label a user must read", and this line carries the coverage
 * fact, the denominator and the never-an-inferred-cross-tab caveat — the exact
 * honesty vocabulary that is Mora's differentiator over marketer.com. Setting
 * it below the readability floor would have deleted it more effectively than
 * removing it. At full muted it measures **4.78:1 / 7.16:1**.
 *
 * The rung is still the quietest on the card: it is the only 10px text there,
 * and it sits two size steps under the datum. Size and weight carry the
 * quietness; opacity does not have to, and per P14 opacity may never be the
 * axis that carries a role distinction on its own anyway.
 */
export const ANALYTICS_CARD_FOOTER = 'text-micro tabular-nums text-muted-foreground';

// =============================================================================
// The metric value/label pair. Source of truth: MetricSummaryCards.tsx:237
// (value) and :135 (label) — /insights' canonical KPI-tile hierarchy.
//
// A hand-derived copy of the value string once shipped in
// `ReachStatStrip.tsx:20` ("from memory of /insights' hierarchy", per its own
// comment): `text-xl font-semibold leading-none tracking-tight tabular-nums`.
// Two defects, both invisible in isolation, both visible the moment the two
// surfaces sit side by side: `font-semibold` where the canonical is
// `font-medium`, and no `sm:text-2xl` step, so the Reach number never grew on
// larger screens the way every other KPI tile does. `ReachStatStrip` and
// `AdminStatTile` both import these tokens directly now (2026-08-26) — do not
// let a third hand-typed copy return.
//
// The rule: a metric value has exactly one definition. If you are typing
// `text-lg font-...` for a number, you want STAT_VALUE, not a fresh string.
//
// 🔴 SIZE 20/24px → 18/20px, 2026-08-27, and the reason is a MEASUREMENT, not
// a preference. Both surfaces read live at 1440x900 on the same day:
//
//   marketer.com KPI tile   value 18px/500 · label 12px/500 → ratio 1.50
//   Mora KPI tile (before)  value 24px/500 · label 11px/400 → ratio 2.18
//
// A 24px figure over an 11px label is not "a bigger number", it is a 45%
// louder relationship than the reference the founder called "absolutely
// perfect" — and it is what "way too much emphasis on certain things" is
// pointing at. The fix belongs on the VALUE, not the label: dropping the label
// further would push an 11px/400 line under the readability floor, and raising
// it to marketer's 12px/500 is the "too bold" complaint itself. At 18/20px the
// ratio is 1.82, the closest this scale reaches without inventing a 13px step.
// =============================================================================

/** The metric NUMBER — MetricSummaryCards.tsx:237. */
export const STAT_VALUE = 'text-lg font-medium leading-none tracking-tight tabular-nums sm:text-xl';

/** The small label above/beside a metric number — MetricSummaryCards.tsx:135. */
export const STAT_LABEL = 'text-xs font-medium text-muted-foreground';

// =============================================================================
// THE CARD ARCHETYPE VOCABULARY — the tokens every archetype is an instance of.
//
// Contract: `docs/planning/mora-growth-intelligence-overhaul-2026-08-26/
// 46-CARD-ARCHETYPES.md`. Components: `src/components/analytics/archetypes/`.
//
// 🔴 WHY THIS BLOCK EXISTS. Two rounds fixed `/insights` CARD BY CARD and the
// founder rejected both. His words, 2026-08-27: *"there should be some type of
// ARCHETYPE STRUCTURE that we would have rebuilt — this is what a card is, this
// is how a thing goes in it, this is what text with a tooltip looks like, this
// is what the title looks like, here is what it looks like if there is one
// number, here is what it looks like if there are no numbers."*
//
// Per-card fixes cannot produce consistency; a vocabulary can. Every value
// below was read off `app.marketer.com` on 2026-08-27 via `getComputedStyle` /
// `getBoundingClientRect` — never from a screenshot and never from memory —
// and then re-expressed in Mora's own scale, because copying marketer's px
// would reopen the "too bold / too much contrast" complaint the last round
// closed. Raw captures: `docs/research/competitors/marketer-com/
// evidence-2026-08-27/ARCH-*`.
// =============================================================================

/**
 * THE HEIGHT CONTRACT — the founder's *"all the tiles are basically the same
 * size"*, made mechanical.
 *
 * Measured, every card, BOTH breakpoints: KPI 94 · standard 320 · hero 376 ·
 * header-stat 124 · status band 78 · ledger 438. Every one is declared, every
 * one is byte-identical at 390 as at 1440 — marketer does not shrink a card on
 * a phone, it collapses the grid to one column and the cards keep their exact
 * desktop heights.
 *
 * ⚠️ `min-h-*`, NOT the `h-*` marketer uses, and the difference is deliberate.
 * A hard height cannot be honest about a body it cannot fit: marketer's own
 * ledger has to break its own rule (`min-h-[376px]`, renders 438) to avoid
 * clipping a composition. A floor gives the row alignment the founder asked for
 * while letting the one archetype that must grow, grow — and it is only a floor
 * because {@link CARD_FILLS_ITS_ROW} + {@link BODY_CENTERS_IN_SPARE_HEIGHT}
 * CENTRE a short body in the slack instead of dumping it at the bottom (design
 * hard rule 22). Without that pair a floor just relocates the dead space this
 * block exists to remove; with it, a short card is deliberate rather than
 * unfinished. `ARCH-20-shopify-profit-margin-…png` shows marketer paying the
 * other price: ~200px of void under `Profit & margin`.
 *
 * These are SCALE values, not arbitrary bracket values: Tailwind v4's dynamic
 * spacing scale makes `min-h-94` = 23.5rem = 376px exactly.
 */
export const ARCHETYPE_MIN_H = {
  /** A1 KPI tile — one number, no body. Measured 94; `min-h-24` = 96. */
  kpi: 'min-h-24',
  /** A2 hero chart — the one full-width series per view. Measured 376 = `min-h-94`. */
  hero: 'min-h-94',
  /** A3 series · A4 ranked list · A5 in-card table · A7 composition · A8 matrix · A9 inset strip. Measured 320 = `min-h-80`. */
  standard: 'min-h-80',
  /** A6 ledger — the ONE archetype allowed to exceed its floor rather than clip a composition. */
  ledger: 'min-h-94',
  /** A10 status band — full width, above the first figure. Measured 78; `min-h-20` = 80. */
  band: 'min-h-20',
  /** A card whose whole body is a single header stat. Measured 124 = `min-h-31`. */
  stat: 'min-h-31',
  /** A11 unconfigured section · A12 definition block — NOT cards, no floor. */
  intrinsic: '',
} as const;

export type CardArchetypeHeight = keyof typeof ARCHETYPE_MIN_H;

/**
 * The header row every archetype's title sits in. Height so a card's chrome
 * cannot be set by the length of its own name.
 *
 * 🔴 THE 18px IS MEASURED, NOT CHOSEN. Read off marketer's running DOM at
 * 1440x900 on `?channel=meta` (`SPEC-01`): `class="flex min-w-0 items-center
 * justify-between gap-2 h-[18px]"`, and the SAME 18px appears on a 94px KPI
 * tile and on a 376px chart card. That is the mechanism behind "every card has
 * the same frame" — two cards with different title lengths still start their
 * bodies on the same y. Until 2026-08-27 this constant carried no height at
 * all while its own comment asserted one, which is the "a comment asserting
 * consistency is not a guard" failure exactly.
 *
 * ⚠️ `min-h-4.5`, NOT the `h-[18px]` marketer uses, and the difference is
 * measured rather than timid. `mobile-enhancements.css` raises `.text-caption`
 * to a 20px line box at ≤768px, and `UnconfiguredSection` renders a
 * caption-register label in this row — a hard 18px would clip it on a phone.
 * A floor gives the alignment (the title register, `text-detail`, is a 16px
 * line box at every breakpoint, so the floor IS the height on every card) and
 * cannot crop a body it does not fit. Same rule, same reason, as
 * {@link ARCHETYPE_MIN_H}.
 *
 * Guarded by `card-frame.title-row.guard.test.tsx`.
 */
export const CARD_HEADER_ROW = 'flex min-h-4.5 min-w-0 items-center justify-between gap-2';

// =============================================================================
// THE INSIGHTS GRID — ONE SPINE, TWO MODULES.
//
// 🔴 THIS IS THE ARITHMETIC BEHIND "ours reads as thirty bespoke boxes".
// Measured at 1440 on `/insights?view=overview` (`SPEC-04`), Mora runs THREE
// grid modules that share only the page's left edge (x=256):
//
//     5-up KPI row    lefts 256 · 490 · 725 · 959 · 1194   pitch 234.5  gutter 12.5
//     3-up card rows  lefts 256 · 647 · 1037               pitch 390.5  gutter 11.5
//     2-up card rows  lefts 256 · 842                      pitch 586    gutter 12
//
// Three gutters, no shared vertical line for the eye to hold. marketer runs
// two modules over ONE measure with ONE 20px gutter, and every card edge lands
// on a module edge.
//
// The fix is not "add a fourth module". It is that BOTH modules are expressed
// as spans of a single 12-column grid, so they cannot drift apart — a tile is
// 3 columns and a card is 4, and the two share every gutter and every edge by
// construction:
//
//     column   = (1160 − 11 × 12) / 12  = 85.667px
//     tile     = 3 cols + 2 gutters     = 281.00px   (spec §1.4: 281)
//     third    = 4 cols + 3 gutters     = 378.67px   (spec §1.4: 378.67)
//     two-thirds = 8 cols + 7 gutters   = 769.33px   (= 2 × third + one gutter)
//     full     = 12 cols                = 1160.00px
//
// ⚠️ 🔴 A CARD MAY NEVER DECLARE ITS OWN SPAN OR WIDTH. The span comes from the
// registry row and is applied by `CardFrame`. Guarded by
// `card-frame.one-shell.guard.test.tsx`.
// =============================================================================

/** The one grid every insights card lives in. Gutter is Mora's existing 12px. */
export const INSIGHTS_GRID = 'grid grid-cols-12 gap-3';

/**
 * The four spans a registry row may declare, and nothing else.
 *
 * Breakpoints, per spec §1.4: ≥1280 both modules at full width; 768–1279 the
 * tile module goes 2-up and the card module 1-up; <768 everything 1-up.
 * **Heights are unchanged at every breakpoint** — measured on marketer at
 * 390x844, every card height is byte-identical to 1440. Collapse columns,
 * never heights.
 */
export const CARD_SPAN = {
  tile: 'col-span-12 md:col-span-6 xl:col-span-3',
  third: 'col-span-12 xl:col-span-4',
  'two-thirds': 'col-span-12 xl:col-span-8',
  full: 'col-span-12',
} as const;

export type CardSpan = keyof typeof CARD_SPAN;

/**
 * The body slot's horizontal inset, and the one place this spec deliberately
 * IMPROVES on marketer rather than cloning it.
 *
 * marketer runs two shell padding regimes — `px-4` on the KPI shell, `px-0` on
 * the chart shell — so their shell is not quite one thing. Ours has one shell
 * that contributes VERTICAL padding only, and the BODY supplies its own
 * horizontal inset. That single move is what makes a 50-row table and a 94px
 * tile the same component: a `table` body sets `flush` and its rows run edge to
 * edge under the hairline, a `stat` body sets `default`, and neither needs a
 * different shell.
 *
 * 🔴 `flush` IS `px-2.5`, NOT `px-0` — measured, not zero. `CARD_ROW` already
 * carries its own `px-1.5` (6px) on every row, header included, so a literal
 * `px-0` shell landed the row content 6px from the card edge while the same
 * card's own title sat 16px in (`ANALYTICS_CARD_HEADER`'s `px-4`). Founder,
 * 2026-08-28: *"all the tables should have some left-right padding"* —
 * measured live on `?channel=all`, `Sessions by channel group`'s "Channel
 * group" header cell landed 7px from the card edge against the title's 35px.
 * `px-2.5` (10px) plus the row's own 6px lands the row content at 16px,
 * exactly the title's own inset — one shared left edge for the title and
 * every row beneath it, with zero change to the "no rules, zebra pill only"
 * row grammar this token was built to preserve.
 */
export const CARD_BODY_INSET = {
  default: ANALYTICS_CARD_BODY,
  flush: 'px-2.5 pb-4 pt-0',
} as const;

export type CardBodyInset = keyof typeof CARD_BODY_INSET;

/**
 * 🔴 THE TOP-RIGHT SLOT — the founder's named defect, in one constant.
 *
 * *"the things on the top right of cards that lead elsewhere seem distracting,
 * way too bold — my attention goes there when it should be on the card."*
 *
 * Searched all five marketer channel views: **zero navigation links in any card
 * header.** No "Full breakdown →", no "View all", no "See details". Exactly two
 * things ever occupy a card's top-right, and neither is a link: a header STAT,
 * or a metric switcher that is not in the header at all (it is absolutely
 * positioned OVER the card, with a border one step WEAKER than the card's own).
 *
 * The measured header stat is `text-label-xs font-medium text-fg-subtle` —
 * byte-for-byte the same size, weight and colour as the card title beside it,
 * minus the icon and minus the underline. **Prominence delta versus the title:
 * zero.** That is the whole trick. The top-right is not a place where things
 * are made quiet; it is a place where only quiet things are ALLOWED.
 *
 * So this is not "a quieter token than the title". It is literally
 * {@link ANALYTICS_CARD_TITLE} — composed, not re-typed, so the two cannot
 * drift apart in a later pass. `card-header-affordance.guard.test.tsx` asserts
 * the composition and is proved to fail by giving the stat a weight of its own.
 */
export const CARD_HEADER_STAT = `min-w-0 truncate ${ANALYTICS_CARD_TITLE}`;

// -----------------------------------------------------------------------------
// THE ROW — §3. One primitive under A4, A5 and A6.
//
// 🔴 CORRECTED 2026-08-28 — the zebra fill is RETIRED. It was diagnosed as
// "unboxed marketer parity" against marketer.com's own table, but the founder
// filed the opposite defect against OUR rendering of it (`FOUNDER-FEEDBACK-LEDGER.md`):
//
//   *"the zebra is very light on the alternating rows — or instead just put a
//   little divider line between the rows, because right now it looks like
//   COLUMNS. I can't tell between the rows, and I can't tell the column header
//   from the body."*
//
// The mechanical cause: `CARD_ROW_HEAD` was DEFINED as `CARD_ROW_ZEBRA`, so the
// header row and every odd body row painted the identical fill — there was no
// way to tell them apart because there was, literally, no difference in their
// classes. The fix is not a lighter tint (already tried, already failed per the
// founder) — it is the OTHER option the founder named directly: a hairline rule
// between rows, no fill at all, and a header separator that is visibly STRONGER
// than the row separator so the header reads as a header before the eye even
// gets to the text. The rule must run HORIZONTALLY (a `border-b`, full width of
// the row) so the row rhythm reads across the card, not as vertical columns.
//
// ⚠️ `ui/table.tsx` still owns STANDALONE tables (`/campaigns`, per
// `references/data-tables.md`). It ships `border-b border-border/40` on every
// row and `bg-muted/20` on the header — a standalone-table grammar that reads
// as a box inside a box the moment it is put in a card. Two contracts, on
// purpose. Do not merge them and do not port the sort caret into a card.
// -----------------------------------------------------------------------------

/**
 * One row. Height is the padding + the 16px line box = 36px, uniform, header
 * included. `rounded-lg` is gone: it existed to shape the retired zebra fill
 * into a PILL — an inset shape, never meant to reach the card's edges. A
 * `border-b` rule is a LINE, not a shape, and a rounded inset row would make
 * it visibly stop short of the card's content-column edges rather than read
 * as one continuous rule. `px-1.5` stays: it is the row's own contribution to
 * the hand-measured 16px the row text starts at (10px from the card body's
 * own inset + this 6px — see `AudienceSummarySection.tsx`'s `flush` card),
 * and it is comfortably small enough that the rule still reads as full-width.
 */
export const CARD_ROW =
  'grid min-w-0 grid-cols-[var(--card-row-cols)] items-center gap-2.5 px-2 py-2.5 hover:bg-muted/50 transition-colors';

/**
 * The row separator — a quiet hairline `border-b` between body rows.
 */
export const CARD_ROW_RULE = 'border-b border-border/30';

/**
 * The header separator.
 */
export const CARD_ROW_HEAD_RULE = 'border-b border-border/70';

/** The header row: the same 36px row, at the card title's own register, with a background color. */
export const CARD_ROW_HEAD = `${ANALYTICS_CARD_TITLE} ${CARD_ROW_HEAD_RULE} bg-muted/50 rounded-t-md px-2 py-2 mb-0.5`;

/** A text cell. 400 weight — one step UNDER its own number (see below). */
export const CARD_ROW_LABEL = 'min-w-0 truncate text-caption font-normal text-foreground';

/**
 * A number cell. `font-medium` where the label is `font-normal`: measured, the
 * numbers in a marketer row are exactly one weight heavier than the labels, and
 * that single step is what lets the eye run down a column of figures without a
 * rule to guide it. `tabular-nums` so the column does not jitter.
 */
export const CARD_ROW_VALUE =
  'min-w-0 truncate text-right text-caption font-medium tabular-nums text-foreground';

// -----------------------------------------------------------------------------
// THE EMPTY / NULL VOCABULARY — §6. The founder praised this specifically.
//
// TWO CONVENTIONS RUN SIDE BY SIDE AND THEY MEAN DIFFERENT THINGS:
//
//     `0`  =  we measured, and the answer is none.
//     `—`  =  we did not measure.
//
// A ratio with no denominator prints the null glyph even when its own inputs
// print `0`. Measured on marketer's `Attribution window` card: `Purchases 0`,
// `Revenue $0.00`, but `ROAS —`, `CPA —`. Never print `0` for something you did
// not measure, and never print `—` for a measured zero.
// -----------------------------------------------------------------------------

/**
 * THE NULL GLYPH — "we did not measure this".
 *
 * Marketer prints an ASCII hyphen; Mora has printed an em-dash since
 * `MetricSummaryCards` was written and every existing `/insights` surface reads
 * that way, so the CHARACTER stays Mora's. The RULE is the part that was
 * measured and the part that matters: it is rendered at the VALUE's own size,
 * weight and ink — never dimmed. Marketer's null reads `#dbd8d2` at 18/500, the
 * identical token as a real number beside it.
 *
 * Dimming it is how "we don't know" starts reading as "still loading", which is
 * the one thing a null must never be confused with.
 */
export const NULL_GLYPH = '—';

/**
 * The empty BODY — one sentence, centred on both axes, filling the card's whole
 * remaining height. `49-CHART-DOCTRINE.md` CD-40 (`empty` / `unmeasured`).
 *
 * `MEASURED` on `app.marketer.com/dashboard/analytics?channel=all` at 1440,
 * 2026-08-27, through `getComputedStyle` on the running DOM — not a screenshot:
 * the empty chart card is 320x748, its body is
 * `flex min-h-0 flex-1 items-center justify-center px-6 text-center` at 250px,
 * and the sentence is 13px/450/`rgb(120,119,117)`, centred on BOTH axes. No
 * skeleton, no icon, no illustration, no button, no big slash. Founder,
 * 2026-08-19, on a house LineIcon in a card body: *"what does that random graph
 * icon there do?"* Nothing.
 *
 * ── 🔴 `only:h-full` IS THE FIX FOR A SILENT NO-OP, NOT A BELT-AND-BRACES ────
 * `flex-1` only does anything when the PARENT is a flex container. Three of the
 * four cards on `?channel=shopify` put this token inside a plain BLOCK box —
 * `framed-card`'s `CardContent` renders `min-w-0 flex-1` with `display: block`,
 * so it claims 260px from the frame and cannot hand a single pixel of it to its
 * child. `MEASURED` live at 1440x900 2026-08-27 before this change:
 * `revenue-by-channel` drew an 18px sentence at y=43 inside a 320px card with
 * **259px of dead ground beneath it**, and `whos-buying` did the same at 54px.
 * The class was present the whole time and did nothing — the repo's #1 recurring
 * bug class, in a token.
 *
 * `only:h-full` (`:only-child`) states the rule exactly: **an empty sentence
 * that IS the whole body fills the whole body**, whatever the parent's display.
 * It is scoped to `:only-child` deliberately — in `ChartFrame`'s empty branch
 * the sentence has a `footer` sibling, and an unconditional `h-full` would take
 * 100% of the body and push that footer out of the card. Guarded by
 * `analytics/__tests__/chart-empty-state.test.tsx`.
 *
 * ── THE MEASURE CAP ─────────────────────────────────────────────────────────
 * `max-w-prose` (65ch) because a centred sentence has no line-start anchor: past
 * ~75 characters the eye loses the return sweep and re-reads the same line.
 * Marketer never needed one — its sentence is `No data for this date range`, 162px
 * wide. Mora's empties are specific, and `revenue-by-channel` is a 769px card.
 */
export const CARD_EMPTY_SENTENCE =
  'flex min-h-0 flex-1 only:h-full items-center justify-center px-6 text-center text-caption font-medium text-muted-foreground [&>p]:max-w-prose';

/**
 * The `unavailable` BODY — CD-40's third state: "we cannot measure this",
 * distinct from `empty`'s "not yet".
 *
 * ── 🔴 IT DOES NOT COLLAPSE INSIDE A ROW, AND CD-40 AS WRITTEN IS WRONG ABOUT
 *    THAT. This is a correction MEASURED on the built thing, not an argument.
 *
 * The doctrine's reasoning is sound — there is no future render to reserve space
 * for — and its evidence is marketer's `Conversion metrics`, which is **demoted
 * out of the card grid entirely**: a bare full-width section under the last row.
 * Collapsing a card that stays IN a two-up row is a different move with a
 * different result. `MEASURED` live at 1440x900 on `?channel=shopify`
 * 2026-08-27, with the collapse implemented: `Storefront funnel` came down to
 * 128px beside a 320px row-mate and left **192 x 574px of bare ground** under
 * it — a larger, uglier void than the 209px of dead card it was removing, on a
 * page whose founder note reads *"the cards need to fill the space."* Half of
 * marketer's rule, and the worse half.
 *
 * Mora already HAS marketer's demotion, and it is not this component: the
 * `Not built yet for this channel: …` line closing every grid
 * (`InsightsCardGrid.PlannedCardsNote`) is exactly that shape, for exactly that
 * class of statement. `unavailable` is a different fact — a card that exists and
 * WOULD measure the moment a scope is granted — so it keeps its cell and its
 * height, and the grid stays regular.
 *
 * ── WHAT DISTINGUISHES IT INSTEAD (CD-41, without geometry) ────────────────
 * 1. LEFT alignment against `empty`'s centred sentence. Text alignment is an
 *    edge cue and is read pre-attentively, before a word of it is parsed.
 * 2. No ghost axis — the card is not promising a plot region.
 * 3. The recovery action, when one exists, in the warning ink. Hue plus an
 *    underline is a stronger glance-level signal than 200px of height ever was.
 *
 * `only:h-full` for the same reason {@link CARD_EMPTY_SENTENCE} carries it: the
 * `flex-1` is inert inside `framed-card`'s block body.
 */
export const CARD_EMPTY_UNAVAILABLE =
  'flex min-h-0 w-full flex-1 only:h-full flex-col items-start justify-center gap-2 px-1 text-left text-caption font-medium text-muted-foreground [&>p]:max-w-prose';

/**
 * THE CAVEAT LINE — one line of amber text at the card foot when a figure is
 * WITHHELD or degraded.
 *
 * Measured: 12px/450/`#e87800`, PLAIN TEXT. No panel, no icon, no border, no
 * fill, no rounded box. That restraint is the whole point — a tinted callout is
 * a second object competing with the measurement, and it is exactly the
 * "classic AI slop" pattern the founder has already rejected once. Say the
 * coverage, say what was withheld, stop.
 */
export const CARD_CAVEAT_LINE = 'text-detail font-normal text-warning';

/**
 * A9 INSET STAT TILE — 4-6 small facts that belong to one subject and do not
 * each deserve a card.
 *
 * 🔴 AN INSET STAT IS NOT A KPI TILE, and confusing the two is precisely how a
 * page ends up with eight KPI tiles. Measured differences, all three
 * deliberate: its value is 13px where a KPI's is 18px; it has a FILL and NO
 * BORDER where a card has a border and no fill; and it never carries a
 * sparkline or a delta.
 */
export const CARD_INSET_TILE = 'flex min-w-0 flex-col gap-1 rounded-lg bg-muted/40 p-3';
export const CARD_INSET_LABEL = ANALYTICS_CARD_TITLE;
export const CARD_INSET_VALUE = 'truncate text-caption font-medium tabular-nums text-foreground';

// =============================================================================
// Navigable item card. The compact project/document card archetype is a
// self-contained destination, so its resting edge must remain perceptible on
// the light canvas. Keep this separate from PANEL_CONTAINER (sectioned
// surfaces) and from the paper-on-table artifact archetype (Library/Workflows).
// =============================================================================

export const NAVIGABLE_ITEM_CARD =
  'group relative flex min-h-[120px] cursor-pointer flex-col rounded-lg border border-border/50 bg-card/40 p-4 text-left transition-colors hover:border-border/60 hover:bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Compact full-width action row used when a dialog asks the user to pick a path. */
export const CHOOSER_OPTION_ROW =
  'group flex w-full items-center gap-4 rounded-lg border border-border/50 bg-card/40 p-3 pr-4 text-left transition-colors hover:border-border/60 hover:bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60';

/** First-run tile that begins work, distinct from a card that opens an existing item. */
export const STARTER_ACTION_CARD =
  'group flex min-h-[120px] w-full flex-col items-start rounded-lg border border-border/50 bg-card/40 p-4 text-left transition-colors hover:border-border/60 hover:bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60';

// =============================================================================
// Selectable choice card — the card-sized sibling of SELECTABLE_PILL for
// single-select choices too rich for a pill (icon + title + description).
// Selection carries the same language as the pill: border-foreground/40 +
// bg-sidebar. NO radio circles, NO checkmarks, NO bg-primary. Left-aligned
// content. Consumers: onboarding business-type picker, BrandVoicePathPicker.
// =============================================================================
export const SELECTABLE_CHOICE_CARD =
  'group relative flex flex-row items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed sm:flex-col sm:items-start sm:gap-1.5';
/** Apply when the choice card is selected. Same selection grammar as
 *  SELECTABLE_PILL_ON (Discover preferences): strengthened neutral border +
 *  filled ground — one selection language app-wide. The earlier
 *  `ring-2 ring-primary` treatment shouted against every other selected
 *  surface (JT: "a ton of contrast") and is retired; keyboard focus remains
 *  the separate focus-visible ring-ring, so nothing collides. */
export const SELECTABLE_CHOICE_CARD_ON = 'border-foreground/40 bg-sidebar';
/** Apply when the choice card is not selected. */
export const SELECTABLE_CHOICE_CARD_OFF =
  'border-border/50 bg-card/40 hover:border-border/60 hover:bg-card/60';

/** Media-forward card for a generated destination such as a Content Board draft. */
export const CONTENT_PREVIEW_CARD =
  'group relative flex flex-col overflow-hidden rounded-lg border border-border/50 bg-card/50';
export const CONTENT_PREVIEW_CARD_INTERACTIVE =
  'cursor-pointer transition-colors hover:border-border/60 hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Shared interaction layer for image- and video-forward cards. */
export const MEDIA_CARD_INTERACTION =
  'cursor-pointer transition hover:border-border hover:shadow-elevation-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The hover response of a card whose whole surface opens it: the media eases
 * forward inside its own clip. One value for every clickable media card —
 * Discover articles and the Content ideas board used to answer the same hover
 * differently (a zoom on one, nothing on the other). Needs a `group` and an
 * `overflow-hidden` ancestor; `motion-safe` honours reduced motion.
 */
export const CARD_MEDIA_ZOOM =
  'transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]';

/** Base chrome for horizontally scrolling image/video starter cards. */
export const MEDIA_STARTER_CARD =
  'group relative shrink-0 snap-start overflow-hidden border border-border/50 bg-muted/40 text-left ' +
  MEDIA_CARD_INTERACTION;

// =============================================================================
// Artifact preview stage — the shared "media on a quiet table" treatment for
// Inspector image/video viewers and social-post previews.
//
// The editable social-preview stage retains the focused canvas semantic. Plain
// media inspection uses NEUTRAL_ARTIFACT_PREVIEW_STAGE below so an image dialog
// and its composer remain one continuous surface.
// =============================================================================

export const ARTIFACT_PREVIEW_STAGE = 'bg-canvas';
/** Neutral inspection surface for image/video/PDF dialogs with adjacent chrome. */
export const NEUTRAL_ARTIFACT_PREVIEW_STAGE = 'bg-background';
/** Raw image/video edge against the preview stage. */
export const ARTIFACT_PREVIEW_OBJECT =
  'ring-1 ring-border/40 dark:ring-foreground/10 shadow-elevation-1';
/**
 * Social replicas already draw their own platform chrome. Elevate the replica
 * as one foreground artifact without adding a second generic outline.
 */
export const ARTIFACT_PREVIEW_CARD = 'shadow-elevation-1';

// =============================================================================
// UNDERLINE SUB-NAV TABS — shared by the Library (Artifacts/Faces) strip and
// the Analytics view tabs so the two stay pixel-identical (sibling parity).
//
// Type sizing: tab text is `text-caption` (12px), a deliberate step DOWN from
// the 14px page breadcrumb/title so the sub-nav never out-shouts the page
// location above it. The active tab's `border-b-2` overlaps the nav baseline
// via `-mb-px`, so the underline sits ON the line with no gap.
// =============================================================================
//
// Focus ring is INSET (`focus-visible:ring-inset`). An outset ring on a tab
// whose nav is `overflow-x-auto` (mobile scroll) gets clipped by that scroll
// container, so keyboard focus drew a broken half-rectangle around the tab —
// "a really weird focus ring that looks terrible" (founder review 2026-07-24).
// An inset ring is drawn inside the tab's own box, so it can never be clipped,
// and it stays monochrome `ring-ring` (never `ring-primary`, which is
// selection state) and `focus-visible:` only, so a mouse click leaves nothing
// behind.
export const UNDERLINE_TAB =
  '-mb-px inline-flex h-8 shrink-0 items-center rounded-t-sm border-b-2 px-3 text-caption font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
export const UNDERLINE_TAB_ACTIVE = 'border-foreground text-foreground font-medium';
export const UNDERLINE_TAB_INACTIVE =
  'border-transparent text-muted-foreground hover:text-foreground';
// Nav strip when the tabs own the whole row (Library). Analytics shares its
// baseline with right-aligned controls via the parent flex (items-end) and so
// sets `border-b` on the parent + passes `border-b-0` to the nav instead.
export const UNDERLINE_TAB_NAV = 'flex items-center gap-1 border-b border-border/60';

// =============================================================================
// TEXT OVERFLOW — single-line long text fades out on the right instead of a
// hard "…" ellipsis. The Mora-canonical overflow treatment (matches the
// breadcrumb/sidebar title fade): softer, never clips a glyph mid-stroke, and
// reads as "there's more" without the dot-noise. Pair with a flex/min-w-0
// parent. Fades the last ~28px to transparent (works on any background since a
// mask, not a bg gradient). Documented in DESIGN-PATTERNS §Text overflow.
// =============================================================================
export const TEXT_FADE_RIGHT =
  'overflow-hidden whitespace-nowrap [mask-image:linear-gradient(to_right,black,black_calc(100%-1.75rem),transparent)]';

// =============================================================================
// HORIZONTAL SCROLL EDGE FADE — a row that scrolls sideways fades only the edge that still has
// more row behind it, so a half-cut word reads as "scroll", not as a clipped label. Same 16px
// mask gradient `FilterChipRail` draws on mobile, but driven by `useScrollEdges` (measured
// overflow) instead of being always on. Pick the class from the two measured flags.
// =============================================================================
export function scrollEdgeFade(edges: { start: boolean; end: boolean }): string | undefined {
  if (edges.start && edges.end) {
    return '[mask-image:linear-gradient(to_right,transparent_0,black_16px,black_calc(100%-16px),transparent_100%)]';
  }
  if (edges.end)
    return '[mask-image:linear-gradient(to_right,black_calc(100%-16px),transparent_100%)]';
  if (edges.start) return '[mask-image:linear-gradient(to_right,transparent_0,black_16px)]';
  return undefined;
}

// =============================================================================
// BREADCRUMB COLOR SCALE — the ONE place a page-header breadcrumb's root vs.
// leaf color is declared. Root = muted background context ("Library",
// "Projects", "Discover"); leaf = the active/current segment, one step below
// the shared `<h1>`'s own `text-foreground/90`. Every breadcrumb-shaped title
// across the app (RouteBreadcrumb, InspectorBreadcrumb, CalendarHeaderTitle,
// ArticleDetailPage's inline crumb) should import these rather than picking
// its own opacity value — that's how /projects (text-foreground/85) and
// /library (text-muted-foreground) drifted out of sync despite both being
// "just a breadcrumb root" (audit, 2026-07-27).
// =============================================================================
// `max-w-40` (160px, ~20 characters at the header scale) caps the ROOT so the
// LEAF stays readable. Without it both segments carry a bare `truncate` and
// simply split the available width, so a long Brief name ate the header and
// elided BOTH halves — "Year-round tax planning for Texas small business owners
// — S-corp elections,… / Stop setting your payroll salary just once." tells the
// reader nothing about either. A breadcrumb exists to say where you are; the
// leaf is the answer and the root is context, so the root is what gives way.
// Callers still supply `truncate` and `min-w-0` themselves.
export const BREADCRUMB_ROOT_CLASS =
  'max-w-40 tracking-tight text-muted-foreground hover:text-foreground transition-colors';
export const BREADCRUMB_LEAF_CLASS = 'tracking-tight text-foreground/80';
/**
 * Page-header breadcrumbs inherit the responsive title scale of a plain title.
 * The shared Breadcrumb primitive defaults to `text-sm`, so header callers use
 * this override instead of restating typography route by route.
 */
export const PAGE_HEADER_BREADCRUMB_LIST =
  'min-w-0 flex-nowrap justify-center gap-1.5 text-base font-semibold sm:gap-1.5 md:justify-start md:text-sm md:font-medium';

// =============================================================================
// Page-level horizontal gutter — the ONE edge inset for a top-level route's
// scrollable content area, directly below that page's header (Projects,
// Library, Faces, Workflows). Was hand-typed identically in all four and had
// already drifted once (mixed px-4/mx-4/px-4 sm:px-6 across wrapper divs)
// before a fix session reconciled them back to parity. Horizontal-only:
// vertical spacing (`pt-4 pb-32`) and scroll behavior (`overflow-auto` vs
// `overflow-hidden`) vary per page and stay inline at the call site.
// =============================================================================
export const PAGE_GUTTER = 'px-4 sm:px-6';

// =============================================================================
// WARNING_CHIP — the interactive warning affordance (a needs-attention CTA, a
// recovery chip). Amber border + text with the state kept through hover.
//
// ⚠️ The `hover:` re-asserts are LOAD-BEARING, not decoration. shadcn Button
// `outline`/`flat` variants carry `hover:text-accent-foreground`, which
// silently WIPES a bare `text-warning` to near-white at the exact moment the
// user hovers to click — the decision moment. Any status color on those
// variants MUST ship with its `hover:` twin. This bug shipped twice (SlotCard
// re-asserted it; the execution-state CTA first shipped without it). Use this
// constant instead of hand-rolling so it can't regress a third time. The
// destructive equivalent (`text-destructive hover:text-destructive
// hover:bg-destructive/10`) lives inline in SlotCard for now.
// =============================================================================
export const WARNING_CHIP = 'border-warning/40 text-warning hover:text-warning hover:bg-warning/10';

// =============================================================================
// WORKBENCH INSPECTOR — the "hairline + fill + one shadow" recipe.
//
// Ported from docs/research/competitors/hightouch/OBSERVED-2026-08-12-ad-studio.md
// §3.5 and the Concept Workbench spec §5.3, translated into Mora semantic
// tokens. The measured source values are recorded in the WHY comments purely as
// provenance — never author those oklch literals into the app.
//
// The point of the recipe: property rows are FILLS, not bordered inputs. A
// 360px inspector holding ~14 bordered rows becomes 14 competing rectangles;
// the same 14 rows as quiet fills add zero edges. Structure comes from
// alignment and one-edge hairlines, elevation is spent on exactly ONE element
// (the floating prompt bar), and colour is exactly one accent at ~4–10%.
// =============================================================================

/**
 * The universal inspector property row. Label left, value right, filled, 32px,
 * ZERO border.
 * WHY (measured, Hightouch): height 32px; background oklch(0.9702 0 355);
 * border none; border-radius 5px; padding 0 10px. Mora uses `rounded-sm` (8px)
 * rather than a one-off 5px — the radius rhythm is a system, not a copy.
 */
export const PROPERTY_ROW =
  'flex h-8 items-center justify-between gap-2 rounded-sm bg-muted px-2.5 dark:bg-muted/40';

/**
 * The hover / focus lift for an INTERACTIVE property row (a dropdown trigger, a
 * row wrapping a live input).
 *
 * Not a second `bg-muted` step: in light the fill already sits at full
 * `--muted`, so "more muted" is a no-op. `muted-foreground/10` moves in the
 * correct direction in BOTH themes — darker on cream, lighter on zinc — which
 * one fixed tint cannot do.
 */
export const PROPERTY_ROW_INTERACTIVE = 'transition-colors hover:bg-muted-foreground/10';

/**
 * The row's left-hand label. WHY: 11px, oklch(0.4077 0.0031 345.34) — a muted
 * near-neutral that carries a whisper of chroma. Mora's greys already carry
 * warmth, so `text-muted-foreground` is the direct translation.
 */
export const PROPERTY_LABEL = 'text-detail text-muted-foreground shrink-0';

/**
 * The row's right-hand value. WHY: right-aligned, oklch(0.3018 0.004 355) —
 * darker than the label, so the eye reads value-first on a scan.
 * `tabular-nums` keeps a column of numbers from dancing as digits change.
 */
export const PROPERTY_VALUE = 'text-caption text-foreground/85 text-right tabular-nums';

/**
 * The unit suffix (`px`, `°`, `%`). WHY: Hightouch renders the unit as a
 * SEPARATE muted element, not as part of the value string — so the number stays
 * selectable/editable and the unit never reads as data.
 */
export const PROPERTY_UNIT = 'text-detail text-muted-foreground/60 shrink-0';

/**
 * The one-edge hairline that separates inspector sections.
 * WHY: 0.5px solid oklch(0.9401 0 0), one edge only — no cards, no boxes, no
 * outlines anywhere in the chrome. Sub-pixel borders round unpredictably across
 * browsers/DPRs, so Mora expresses "barely there" through OPACITY on a 1px edge
 * (`border-border/40`) rather than a fractional width.
 */
export const WORKBENCH_HAIRLINE = 'border-border/40';

/**
 * Authoring shell: the ONE content column. Breadcrumb, step header, fields,
 * dropzone and the action footer all sit inside this measure with this
 * gutter, so they share a single left edge and a single right edge at every
 * breakpoint. `max-w-3xl` includes the gutter (border-box), so the visible
 * column is 688px at `lg`. Never wrap a step body in a second frame: nested
 * frames each add a gutter and the step content drifts off the header's edge.
 */
export const AUTHORING_COLUMN_WIDTH = 'mx-auto w-full max-w-3xl';

/** Horizontal gutter of {@link AUTHORING_COLUMN_WIDTH}; shared by the content frame and the footer. */
export const AUTHORING_COLUMN_GUTTER = 'px-5 sm:px-8 lg:px-10';

/**
 * Optical alignment for a `flat` `sm` button that sits on a column edge: the
 * button's own `px-3` inset would otherwise push its label 12px inside the
 * column. Pull the box out so the LABEL meets the edge (leading side).
 */
export const AUTHORING_EDGE_FLAT_START = '-ml-3';

/** Trailing-side counterpart of {@link AUTHORING_EDGE_FLAT_START}. */
export const AUTHORING_EDGE_FLAT_END = '-mr-3';

// =============================================================================
// Channel colour — Meta, Google Ads, LinkedIn.
//
// For a channel's MARK and its split-bar FILL only (one-family sheet, notes
// "Language"). Never a state, a heading or a button: state keeps StatusBadge
// tones, and a channel hue on a state would read as "this is Meta" and "this
// is on" at once. A bar always carries the channel name and its percentage,
// so the hue is never the only signal.
//
// Values live in `src/index.css` (`--channel-*`, light and dark). Lightness is
// set so the three hues stay apart in greyscale; the check is
// `src/lib/__tests__/channel-colour-tokens.test.ts`. Class names are written
// out in full so Tailwind can see them.
// =============================================================================
export type ChannelColourKey = 'meta' | 'google' | 'linkedin';

/** Fill for a split-bar segment. */
export const CHANNEL_FILL: Record<ChannelColourKey, string> = {
  meta: 'bg-channel-meta',
  google: 'bg-channel-google',
  linkedin: 'bg-channel-linkedin',
};

/** Colour for a channel's mark (an icon drawn with `currentColor`). */
export const CHANNEL_MARK: Record<ChannelColourKey, string> = {
  meta: 'text-channel-meta',
  google: 'text-channel-google',
  linkedin: 'text-channel-linkedin',
};

/**
 * Campaign page (measure) motion, on the transitions.dev scale. Usage decides the token: a verdict or
 * status word swaps in 150ms; a changed figure pops in over 500ms and never on first paint; a read that
 * arrives reveals over 400ms. Only opacity and transform animate, and reduced motion removes every one.
 * The gauge and the bars are drawn in SVG with percentage coordinates, so they carry no inline style and
 * no motion: moving them would need one.
 */
export const MEASURE_TEXT_SWAP_MOTION =
  'animate-in fade-in-0 duration-150 ease-fluid motion-reduce:animate-none';
export const MEASURE_FIGURE_POP_MOTION =
  'animate-in fade-in-0 zoom-in-97 duration-500 ease-fluid motion-reduce:animate-none';
export const MEASURE_REVEAL_MOTION =
  'animate-in fade-in-0 duration-400 ease-fluid motion-reduce:animate-none';

/** The verdict word and its figure take the verdict colour; nothing else on the campaign page does. */
export const MEASURE_TONE_TEXT = {
  good: 'text-success',
  warn: 'text-warning-ink',
  bad: 'text-destructive',
} as const;
export const MEASURE_TONE_FILL = {
  good: 'fill-success',
  warn: 'fill-warning-ink',
  bad: 'fill-destructive',
} as const;
